import type { DeferredBookingAccessDto, DeferredMembershipPurchaseDto } from '@zipper/embedded-api-client';
import { prepareDeferredBooking } from '../../payments/deferred-checkout';
import {
  LitElement,
  html,
  noChange,
  nothing,
  svg,
  type PropertyValues,
  type TemplateResult,
} from "lit";
import { customElement, property } from "lit/decorators.js";
import { consume } from "@lit/context";
import { createActorState, type ActorStateOf } from "../../state/actor-state";
import { live } from "lit/directives/live.js";
import { applyAccentVars } from "../../theme/theme";
import { bookingCheckoutStyles } from "./booking-checkout/booking-checkout.styles";
import { scrollRootBy, scrollToStart } from "./composed-layout";
import { TAKEN_SEATS, isPack, type PricingTier } from "../data/mock-pricing";
import type { EmbedStripe } from "../../config/types";
import {
  registerGuest,
  registerSelf,
  registerFamilyMember,
  createGuestRoster,
  isAccountExistsError,
  type ChargeItem,
  type GuestRosterHandle,
} from "../../payments/stripe-client";
import { accountExistsMessage } from "../../checkout/account-exists-copy";
import {
  createSpotHold,
  fetchSpotAvailability,
  releaseSpotHold,
  validatePromotionCode,
  promoDiscount,
  fetchMemberContext,
  fetchEventRegistrationContext,
  fetchAppointmentRegistrationContext,
  fetchAppointmentPaymentOptionsAnon,
  fetchClassPaymentOptionsAnon,
  fetchAppointmentForm,
  fetchFormsOnFile,
  submitAppointmentForm,
  type FormSubmitIdentity,
  fetchProductCatalog,
  tiersFromPaymentOptions,
  updateMemberPhone,
  type RoomAvailability,
  type SpotAvailability,
  type MemberContext,
  type EmbedForm,
  type FormQuestion,
  type EmbedFamilyMemberWithRoster,
} from "../../data/live";
import "./zipper-payment";
import type { BookingPaymentPrerequisites, ZipperPayment } from "./zipper-payment";
import "./zipper-confirmation";
import { openSignIn } from "./open-signin";
import { clearEmbedToken } from "../../config/client";
import * as checkoutMath from "../../checkout/checkout-math";
import { formatCents, type OrderTotals } from "../../checkout/order-totals";
import { formatShortAmount } from "../format-money";
import type { RequiredSignupFields } from "../../config/types";
import {
  emptyRequiredProfile,
  isPhoneRequired,
  isRequiredProfileComplete,
  missingRequiredProfileFields,
  REQUIRED_PROFILE_ERRORS,
  dateOfBirthError,
  requiredProfilePayload,
  resolveRequiredSignupFields,
} from "../../checkout/required-profile";
import { CheckoutSubmissionController } from "../../checkout/submission.machine";
import {
  pickBestMemberCredit,
  isMemberCreditUsable,
  isMemberCreditShareableTo,
  sortMemberCredits,
  type MemberCreditOption,
} from "../../checkout/member-credits";
import {
  BUY_PREFIX,
  MEMBER_DROPIN_TIER_ID,
  memberTiers,
  ownedAccess,
  tierVisibleTo,
  tierVisibleToAttendee,
  withFamilyAccess,
  familyAccessProductId,
} from "../../checkout/owned-access";
import {
  createPublicEmbedRuntime,
  embedRuntimeContext,
  runtimeApiArguments,
  type EmbedRuntime,
} from "../../runtime/context";
import {
  renderBookingConfirmation,
  renderBookingSummary,
  renderIntakeQuestion,
  renderOtherAttendees,
  renderPaymentChoices,
} from "./booking-checkout/booking-checkout.views";
import {
  BOOKING_STEPS as STEPS,
  DEFAULT_WAIVER_PARAGRAPHS as WAIVER_PARAS,
  MAX_EXTRA_ATTENDEES as MAX_EXTRA,
  RECIPIENT_CHOICES,
  emptyAttendee,
  toParagraphs,
  type Attendee,
  type RichFormQuestion,
} from "./booking-checkout/booking-checkout.model";
import {
  renderBookingWaiver as renderBookingWaiverView,
  renderFamilyWaiver as renderFamilyWaiverView,
} from "./booking-checkout/booking-waiver.view";
import { renderBookingSeats } from "./booking-checkout/booking-seats.view";
import {
  renderAttendeeFields,
  renderBookingDetails,
} from "./booking-checkout/booking-attendees.view";

/**
 * `EntityFormRequirement.AlwaysRequire` — the form is signed from scratch every
 * booking, so nothing on file can satisfy it.
 */
const ALWAYS_REQUIRE = "always-require";

/** PLAT-6805: one family member's own membership verdict for this activity. */
interface FamilyAccess {
  freeProductIds: string[];
  ownedProductIds: string[];
  memberDropInPrice: number | null;
  deferredMemberships?: ReturnType<typeof ownedAccess>['deferredMemberships'];
}

const bookingCheckoutActorState = createActorState(() => ({
  payReady: false,
  quote: null as OrderTotals | null,
  roomAvail: null as RoomAvailability | null,
  seatError: null as string | null,
  formsResolved: false,
  formsError: false,
  touchedFields: new Set<string>() as ReadonlySet<string>,
  activeStep: 1,
  done: new Set<number>(),
  buyer: { ...emptyAttendee(), tierId: "dropin" } as Attendee,
  requiredProfile: emptyRequiredProfile(),
  forSomeoneElse: false,
  recipientType: "" as "" | "family" | "guest",
  recipientModalOpen: false,
  includeSelf: true,
  people: [emptyAttendee()] as Attendee[],
  activeSpot: 0,
  discount: 0,
  placedOrderId: "",
  signedInName: "",
  memberCtx: null as MemberContext | null,
  regCredits: [] as MemberCreditOption[],
  ownedAccessProductIds: [] as string[],
  ownedProductIds: [] as string[],
  memberDropInPrice: null as number | null,
  /** Buyer's pending reservations for this activity, by product id. */
  deferredMemberships: undefined as FamilyAccess['deferredMemberships'],
  /** PLAT-6805: each family attendee's own membership access, by user id. */
  familyAccess: {} as Record<number, FamilyAccess>,
  familyMembers: [] as EmbedFamilyMemberWithRoster[],
  waiverOpen: false,
  waiverChecked: false,
  waiverOnFile: false,
  familyWaiver: {} as Record<
    number,
    { on: boolean; open: boolean; agreed: boolean }
  >,
  waiverForm: null as EmbedForm | null,
  termsForm: null as EmbedForm | null,
  intakeForms: [] as EmbedForm[],
  productForms: [] as EmbedForm[],
  intakeAnswers: {} as Record<string, string>,
  intakeError: "",
  bookingError: "",
  promoCode: "",
  appliedCouponId: "",
  promoError: "",
  promoBusy: false,
  termsOpen: false,
  termsChecked: false,
  termsConsents: {} as Record<number, boolean>,
  buyOptionsOpen: false,
  step1Touched: false,
  apptPayResolved: false,
  apptTypeIsFree: false,
  resolvedServiceProductId: "",
  resolvedServicePrice: 0,
  apptPayError: "",
  classPayPending: false,
  classPayError: "",
}));

type BookingCheckoutActorState = ActorStateOf<typeof bookingCheckoutActorState>;

// Inline icons (handoff SVGs).
const ICON_DOC = svg`<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="15" x2="15" y2="15"/></svg>`;
const ICON_CHEVRON = svg`<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>`;
const ICON_CHECK = svg`<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>`;
const ICON_SIGNIN = svg`<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>`;

/**
 * Multi-spot booking checkout — ported from the handoff `stepper-multi.jsx`.
 * Two-column (stepper + sticky Order Summary), embed-friendly collapsible
 * stepper: Your details → Pick your spots → Choose how to pay → Payment.
 * Supports booking for family/guests, a studio seat grid (one spot per
 * attendee), per-person payment, and a shared family pack. Rendered by
 * <zipper-schedule> when a class is booked. Inherits --zui-* theme from host.
 *
 * kind="appointment" renders the single-service appointment checkout in the
 * SAME two-column layout (header card + sticky Order Summary), populated from
 * the appointment fields (`serviceName`, `providerName`, `apptWhen`, `duration`,
 * `price`, `apptLocation`) instead of the class/tier data. Wire
 * <zipper-appointments> to render `<zipper-booking-checkout kind="appointment"
 * …>` so both flows share one checkout instead of the appointment widget's own
 * thinner confirm card.
 */
@customElement("zipper-booking-checkout")
export class ZipperBookingCheckout extends LitElement {
  @consume({ context: embedRuntimeContext, subscribe: true })
  @property({ attribute: false })
  runtime: EmbedRuntime = createPublicEmbedRuntime();
  static styles = bookingCheckoutStyles;

  /** Class being booked (display only). */
  @property({ type: String }) className = "Class";
  @property({ type: String }) classWhen = "";
  @property({ type: String }) instructorName = "";
  @property({ type: String }) duration = "";
  @property({ type: String }) image = "";
  /** Org-configured booking CTA verb (the schedule's own `buttonText`
   * option, e.g. "Reserve spot" instead of "Book class") — kept in sync
   * with the schedule row's own button so the terminology doesn't change
   * mid-flow. Falls back to 'Book class' when unset. */
  @property({ type: String }) bookButtonText = "Book class";

  /**
   * Checkout kind. 'class' (default) renders the multi-spot class booking flow;
   * 'appointment' renders the single-service appointment flow in the same
   * two-column layout, driven by the appointment fields below instead of the
   * class/tier data. Class-only sections (seat grid, per-attendee tiers) are
   * dropped in appointment mode. The "who are you booking for?" family/friend
   * panel IS shared with appointments (respecting `hideRecipientBooking`) so an
   * appointment can be booked for someone other than the signed-in member.
   */
  @property({ type: String }) kind: "class" | "appointment" = "class";
  private readonly submission = new CheckoutSubmissionController(this, "class");
  /** Appointment service name (kind="appointment"); ignored in class mode. */
  @property({ type: String }) serviceName = "";
  /** Appointment provider display name; empty renders as "First available". */
  @property({ type: String }) providerName = "";
  /** Preformatted appointment date/time, e.g. "Wed, Jul 16 · 2:00 PM". */
  @property({ type: String }) apptWhen = "";
  /** Optional appointment location label (rendered in the Order Summary). */
  @property({ type: String }) apptLocation = "";
  /** Flat appointment service price in dollars (class pricing comes from tiers). */
  @property({ type: Number }) price = 0;
  /** Stripe product id for the appointment charge (kind="appointment"). */
  @property({ type: String }) serviceStripeProductId = "";
  /**
   * PLAT-5624: the org's explicit "this service is free" switch
   * (`AppointmentTypeDto.isFree`). Distinct from `price === 0`, which is ALSO
   * what a credit-priced service looks like — see {@link freeBookingAllowed}.
   */
  @property({ type: Boolean }) serviceIsFree = false;
  /**
   * PLAT-5624: post-charge hook that must SUCCEED before this checkout may
   * claim the booking was placed.
   *
   * `<zipper-appointments>` creates the real appointment from its own
   * provider/slot state, and that create can fail (an unresolvable appointment
   * type, a slot taken in the meantime). It used to run off the
   * `zipper-booking-complete` event — i.e. strictly AFTER `placed` had already
   * flipped and the confirmation had rendered — so a failed booking still
   * showed "You're booked!" underneath its own error banner. Wiring it here
   * makes the confirmation conditional on it, and a rejection keeps the
   * checkout on the payment step with a real error instead.
   */
  @property({ attribute: false }) finalizeBooking?: (detail: {
    stripeProductId: string;
    deferredMembershipPurchaseId?: string | undefined;
    deferredBeneficiaryRosterId?: number | undefined;
    checkoutToken?: string | undefined;
    consumeCreditForProductId?: string;
    /** PLAT-6791: the buyer's own All Access membership covering this booking. */
    accessedWithProductId?: string;
    targetUserId?: number;
    currentUserId?: number;
    intakeSubmissions: { formId: number; formResponseId: string }[];
    buyer: {
      firstName: string;
      lastName: string;
      email: string;
      phone: string;
    };
  }) => Promise<void>;
  /** Max attendees per slot (appointment type capacity). */
  @property({ type: Number }) appointmentCapacity = 1;
  /** Live appointment type id (loads pay options when signed in). */
  @property({ type: String }) appointmentTypeId = "";
  /** Provider user id for appointment pay-option lookup. */
  @property({ type: Number }) appointmentProviderUserId = 0;

  @property({ attribute: false }) tiers: PricingTier[] = [];
  private catalogTiers?: PricingTier[];
  private catalogInputTiers?: PricingTier[];
  private catalogSeq = 0;
  private catalogRemovedAllChoices = false;
  private get catalogPending(): boolean {
    return !!this.orgId && !!this.tiers.length && this.tiers !== this.catalogTiers;
  }
  /** Payment context (from embed config), passed through to <zipper-payment>. */
  @property({ attribute: false }) stripe?: EmbedStripe;
  @property({ type: Number }) orgId = 0;
  @property({ type: String }) accent = "#4F9DB5";
  /** ST3: require a valid phone in "Your details" before advancing to payment. */
  @property({ type: Boolean }) requirePhone = false;
  /**
   * PLAT-4456 org policy (`EmbedConfig.requiredSignupFields`), enforced by the
   * API at `create-guest`. Collected in step 1 alongside the buyer's details so
   * a guest is never rejected at the payment step for data we could have asked
   * for at the start.
   */
  @property({ attribute: false }) requiredSignupFields?: RequiredSignupFields;
  /** Org ISO-4217 currency (`EmbedConfig.currency`); USD when unset. */
  @property({ type: String }) currency = "";
  /** Hide the "I'm booking this for someone else" (family/friend) panel. */
  @property({ type: Boolean }) hideRecipientBooking = false;

  protected willUpdate(changed: PropertyValues): void {
    // The schedule can replace its raw tiers after guest/member context loads.
    // Resolve those replacements too, but not our own resolved output.
    if (
      this.hasUpdated &&
      (changed.has("tiers") || changed.has("orgId")) &&
      (changed.has("orgId") || this.tiers !== this.catalogTiers)
    ) {
      void this.enrichTierNames();
    }
    if (changed.has("kind")) {
      this.submission.setKind(this.kind);
    }
    if (this.hasUpdated && changed.has("orgId")) {
      this.memberCtx = null;
      void this.loadMember();
    }
    if (
      changed.has("kind") || changed.has("orgId") || changed.has("orgSlug") ||
      changed.has("eventSlug") || changed.has("activityId")
    ) {
      ++this.classPaySeq;
      this.classPayPending = !!this.preparedClassTarget &&
        this.preparedClassTarget !== this.classTargetKey();
      this.classPayError = "";
      this.payReady = false;
      if (this.hasUpdated && !this.isAppt && !changed.has("orgId")) {
        if (this.memberCtx) void this.loadWaiverStatus();
        else void this.loadClassPayContext(true);
      }
    }
    if (
      (changed.has("appointmentTypeId") ||
        changed.has("appointmentProviderUserId") ||
        changed.has("startIso") ||
        changed.has("orgId") ||
        changed.has("kind")) &&
      this.isAppt
    ) {
      this.familyAccess = {};
      this.familyAccessRequested.clear();
      void this.loadAppointmentPayContext();
    }
    // W1 fail-closed: `classForms` and `orgId` are properties, so a parent can
    // assign them AFTER this element's first render — by which point
    // `firstUpdated()`'s one-shot `loadForms()` has already run, and on the
    // "nothing declared" branch has already flipped `formsResolved`. Left
    // un-armed, a waiver that arrives late is a waiver that is never fetched,
    // never rendered and never signed — while `stepValid(1)`'s C1 fallback
    // (`formsResolved` ⇒ "an empty waiver is nothing to consent to") waves the
    // booking straight through. Re-resolve whenever the declared form set, or
    // the org those form ids belong to, actually changes.
    if (
      this.hasUpdated &&
      (changed.has("classForms") ||
        changed.has("orgId") ||
        changed.has("orgSlug") ||
        changed.has("eventSlug") ||
        changed.has("activityId")) &&
      this.formsKey() !== this.loadedFormsKey
    ) {
      this.rearmForms();
    }
    // Reflect the configured accent onto this host so `var(--zui-accent*)` used
    // inside the checkout (and the light-DOM payment element it renders) matches
    // the embed's accent instead of the baseStyles teal default. See X1.
    if (changed.has("accent")) applyAccentVars(this, this.accent);
    // A server quote belongs to a specific cart. In particular, removing a
    // paying guest can return a member to a cardless zero-rate appointment.
    // That path unmounts payment and must not retain its earlier positive total.
    const quoteContext = JSON.stringify([
      this.orgId,
      this.appointmentTypeId,
      this.startIso,
      this.chargeItems(),
      this.appliedCouponId,
      this.discount,
      this.memberCtx?.rosterId,
    ]);
    if (quoteContext !== this.quotedCartContext) {
      this.quotedCartContext = quoteContext;
      this.quote = null;
    }
  }

  private quotedCartContext = "";

  /**
   * PLAT-5624: appointment pricing resolves asynchronously — after the payment
   * step may already be open. Two things therefore cannot be done once, at
   * mount time:
   *
   *  - a payment element that mounted before pricing landed is holding stale
   *    (unchargeable) items, so re-push the current ones; and
   *  - if the step opened while the total was still 0, nothing mounted the
   *    payment element at all. `renderPayment()` starts rendering the
   *    `#inline-pay-host` slot the moment a real total appears, so mount into
   *    it here rather than leaving the buyer on a payment step with no form.
   */
  protected updated(): void {
    // The buyer can change pricing tier at any point before paying, and each
    // tier can demand a different product form — so this is re-derived on every
    // update. `loadProductForms` no-ops unless the required id set changed.
    void this.loadProductForms();
    // Collapsing step 4 removes `#inline-pay-host` and the card form inside it,
    // but not this reference — and `openPayment()` is idempotent on exactly
    // this field. Without dropping it, anyone who goes back to an earlier step
    // (the "Edit" button, or the bounce in `runBooking`) returns to a payment
    // step that can never mount a card form again. Not while a charge is in
    // flight: that element still owns it, detached or not.
    if (this.paymentEl && !this.paymentEl.isConnected && !this.submitting) {
      this.paymentEl = null;
      if (this.payReady) this.payReady = false;
    }
    if (this.paymentEl) {
      this.syncPaymentItems();
      return;
    }
    if (
      this.activeStep === 4 &&
      !this.placed &&
      this.chargeItems().length > 0 &&
      (this.total > 0 || !!this.deferredSelection) &&
      this.renderRoot.querySelector("#inline-pay-host")
    ) {
      this.openPayment();
    }
  }
  /** Real seat map (live data): room + class-instance window. Empty → generic grid. */
  @property({ type: String }) roomId = "";
  @property({ type: String }) startIso = "";
  @property({ type: String }) endIso = "";
  /** Registration context (live data): set → register each attendee after charge. */
  @property({ type: String }) orgSlug = "";
  @property({ type: String }) eventSlug = "";
  @property({ type: String }) activityId = "";
  /** Entity forms (waiver/intake) attached to this class (live data). */
  @property({ attribute: false }) classForms: {
    formId: number;
    type: string;
    requirement?: string;
  }[] = [];

  /**
   * Mirrors <zipper-payment>'s `canSubmit`, fed by its `zipper-pay-ready`
   * event. The card fields live in Stripe's cross-origin iframe, so this event
   * is the only way to know the embedded form is actually chargeable — hence
   * the CTA greys out until it says so.
   */
  /**
   * The server's price for this cart — the tax it will assess and the platform
   * fee the org passes on. Handed over by `<zipper-payment>`, which previews
   * the charge it is about to make, so the summary and the payment step can
   * never disagree. Null until the booker reaches payment.
   */
  @bookingCheckoutActorState.field("quote")
  private quote!: BookingCheckoutActorState["quote"];

  @bookingCheckoutActorState.field("payReady")
  private payReady!: BookingCheckoutActorState["payReady"];

  private paymentEl: ZipperPayment | null = null;

  @bookingCheckoutActorState.field("roomAvail")
  private roomAvail!: BookingCheckoutActorState["roomAvail"];

  @bookingCheckoutActorState.field("seatError")
  private seatError!: BookingCheckoutActorState["seatError"];
  /** Per-checkout token so the guest can hold/release their own spots. */
  private holderKey = "";

  connectedCallback(): void {
    super.connectedCallback();
    this.holderKey =
      (globalThis.crypto?.randomUUID?.() ?? `zb-${Date.now()}`) + "";
    if (this.roomId && this.orgId && this.startIso && this.endIso) {
      void this.loadSeatMap();
    }
    void this.loadMember();
  }

  /** Load signed-in member context (no-op for guests; null on any failure). */
  private async loadMember(): Promise<void> {
    if (!this.orgId) return;
    const orgId = this.orgId;
    const seq = ++this.memberLoadSeq;
    const ctx = await fetchMemberContext(orgId);
    if (seq !== this.memberLoadSeq || orgId !== this.orgId) return;
    if (!ctx) {
      void this.loadClassPayContext();
      return;
    }
    ++this.classPaySeq;
    this.classPayPending = false;
    this.classPayError = "";
    this.memberCtx = ctx;
    this.familyMembers = ctx.familyMembers;
    if (!this.signedInName) this.signedInName = ctx.firstName || "there";
    // Prefill buyer details from the member when not yet entered.
    if (ctx.email || ctx.firstName) {
      this.buyer = {
        ...this.buyer,
        firstName: this.buyer.firstName || ctx.firstName || "",
        lastName: this.buyer.lastName || ctx.lastName || "",
        email: this.buyer.email || ctx.email || "",
        phone:
          this.buyer.phone ||
          (ctx.phone ? checkoutMath.formatPhoneInput(ctx.phone) : ""),
      };
    }
    void this.loadWaiverStatus();
    void this.loadAppointmentFormsOnFile();
    void this.loadAppointmentPayContext();
  }

  private classPaySeq = 0;
  private memberLoadSeq = 0;
  private preparedClassTarget = "";

  @bookingCheckoutActorState.field("classPayPending")
  private classPayPending!: BookingCheckoutActorState["classPayPending"];

  @bookingCheckoutActorState.field("classPayError")
  private classPayError!: BookingCheckoutActorState["classPayError"];

  private classTargetKey(): string {
    return JSON.stringify([
      this.kind, this.orgId, this.orgSlug, this.eventSlug,
      this.activityId, this.memberCtx?.userId,
    ]);
  }

  /** Only selected, paid lines with unresolved identities need preparation. */
  private get classPricingUnresolved(): boolean {
    const paidItems = this.chargeItems().filter((item) => item.amount > 0);
    return !this.isAppt && paidItems.length > 0 && (
      this.classPayPending || !!this.classPayError || paidItems.some((item) =>
        !item.stripeProductId || item.stripeProductId === "default" || item.stripeProductId === "dropin")
    );
  }

  private applyClassPaymentOptions(
    options: NonNullable<Awaited<ReturnType<typeof fetchClassPaymentOptionsAnon>>>,
  ): void {
    const dropIn = tiersFromPaymentOptions(options).find((tier) => tier.id === "dropin");
    if (!dropIn) return;
    // Member prices are buyer-specific. Other attendees retain the base price;
    // existing memberTiers applies the member's discount/access afterwards.
    const price = options.payment?.priceBeforeDiscount ?? dropIn.price;
    const prepared = { ...dropIn, price, perSession: price };
    this.preparedClassTarget = this.classTargetKey();
    this.tiers = this.tiers.some((tier) => tier.id === "dropin")
      ? this.tiers.map((tier) => tier.id === "dropin" ? { ...tier, ...prepared } : tier)
      : [prepared, ...this.tiers];
  }

  private async loadClassPayContext(force = false): Promise<void> {
    if (
      this.isAppt || this.memberCtx || !this.orgId || !this.orgSlug ||
      !this.eventSlug || !this.activityId ||
      !this.chargeItems().some((item) => item.amount > 0) ||
      (!force && !this.classPricingUnresolved)
    ) return;
    const seq = ++this.classPaySeq;
    const target = this.classTargetKey();
    this.classPayPending = true;
    this.classPayError = "";
    this.payReady = false;
    const options = await fetchClassPaymentOptionsAnon(this.orgId, this.orgSlug, this.eventSlug, this.activityId);
    if (seq !== this.classPaySeq || target !== this.classTargetKey() || this.memberCtx) return;
    this.classPayPending = false;
    if (options) this.applyClassPaymentOptions(options);
    this.classPayError = !options || this.classPricingUnresolved
      ? "We couldn’t confirm this class’s payment options. Please try again."
      : "";
  }

  private async retryClassPayment(): Promise<void> {
    if (this.memberCtx) await this.loadWaiverStatus();
    else await this.loadClassPayContext(true);
  }

  /**
   * Load appointment pay tiers + member credits.
   *
   * PLAT-5624: this used to be signed-in only, which is the single root cause
   * behind BOTH appointment payment defects. A guest's `tiers` stayed empty and
   * the checkout fell back to the public schedule feed's
   * `stripeProductId`/`price` — but the appointment type's Stripe product is
   * created lazily BY this very lookup, and a credit-priced type carries
   * `price: 0` on the type itself. So a guest got either an unchargeable item
   * ("This item isn't available for online purchase yet") or a $0 booking.
   * Guests now resolve the same options through the anonymous endpoint.
   */
  private async loadAppointmentPayContext(): Promise<void> {
    if (!this.isAppt) return;
    // P1: four call sites (willUpdate on type/provider change, loadMember(),
    // the fail-closed $0 re-resolve, and the payment element's user-triggered
    // "Try again") can all be in flight at once, and two of those are driven by
    // clicks the buyer can repeat. Without a generation guard the FIRST
    // response to land last wins, so a stale reply can overwrite `tiers`,
    // `regCredits`, `buyer.tierId` and the resolved Stripe product/price that
    // a newer one already resolved — i.e. charge the buyer off a pricing tier
    // they are no longer on. Newest request issued always wins.
    const seq = ++this.apptPaySeq;
    this.resetAppointmentPayContext();
    if (!this.appointmentTypeId || !this.appointmentProviderUserId) return;

    try {
      if (!this.memberCtx) {
        await this.loadGuestAppointmentPayContext(seq);
        return;
      }
      const ctx = await fetchAppointmentRegistrationContext(
        this.orgId,
        this.appointmentProviderUserId,
        this.appointmentTypeId,
        this.memberCtx.userId,
        this.startIso || undefined,
      );
      if (seq !== this.apptPaySeq) return; // superseded by a newer load
      if (!ctx) {
        this.apptPayError =
          "We couldn’t load the price for this appointment. Please refresh and try again.";
        return;
      }
      this.regCredits = ctx.credits;
      this.applyAppointmentPayOptions(ctx.paymentOptions);
      const tiers = tiersFromPaymentOptions(ctx.paymentOptions);
      this.tiers = ctx.paymentOptions.discountedRateProductId
        ? tiers.map((tier) => tier.id === "dropin" ? {
            ...tier,
            price: ctx.paymentOptions.payment?.priceBeforeDiscount ?? tier.price,
            perSession: ctx.paymentOptions.payment?.priceBeforeDiscount ?? tier.price,
          } : tier)
        : tiers;
      // PLAT-6791: an owned $0 membership books the appointment outright, as
      // the dashboard's "Continue" does (subscriptionPrices price 0 + owned).
      const freeIds = [
        ...new Set(
          (ctx.paymentOptions.subscriptionPrices ?? [])
            .filter((o) => o.ownsProduct && o.price === 0)
            .map((o) => o.productId),
        ),
      ];
      this.ownedAccessProductIds = freeIds;
      this.ownedProductIds = freeIds;
      this.memberDropInPrice = ctx.paymentOptions.discountedRateProductId ? ctx.paymentOptions.payment?.price ?? null : null;
      this.applyOwnedMembershipAccess();
      for (const person of this.people) {
        if (person.familyUserId) void this.loadFamilyAccess(person.familyUserId);
      }
      // D2: `firstUpdated()` calls `enrichTierNames()` once, before this async
      // context resolves — `this.tiers` is still empty at that point, so it
      // no-ops and appointment tiers keep their generic "Membership"/"Class
      // pack" labels forever. Re-run it now that real tiers exist.
      void this.enrichTierNames();
      if (
        tiers.length &&
        !this.buyer.memberAccessProductId &&
        (!this.buyer.tierId || this.buyer.tierId === "dropin")
      ) {
        this.buyer = { ...this.buyer, tierId: tiers[0].id };
      }
      const best = pickBestMemberCredit(ctx.credits);
      if (
        best &&
        !this.buyer.memberCreditProductId &&
        !this.buyer.memberAccessProductId &&
        !this.forSomeoneElse
      ) {
        this.buyer = {
          ...this.buyer,
          memberCreditProductId: best.productId,
        };
      }
    } catch {
      if (seq !== this.apptPaySeq) return;
      this.apptPayError =
        "We couldn’t load the price for this appointment. Please refresh and try again.";
    }
  }

  /**
   * A new appointment-pricing request invalidates every result from the old
   * appointment synchronously, before the first await. Otherwise switching
   * type/provider (or retrying a failed lookup) leaves the previous
   * `apptPayResolved`, free verdict, Stripe product, price and tiers chargeable
   * while the replacement request is pending. Null/rejected responses must
   * remain in this fail-closed state rather than inheriting stale purchase
   * data.
   */
  private resetAppointmentPayContext(): void {
    this.apptPayResolved = false;
    this.apptTypeIsFree = false;
    this.resolvedServiceProductId = "";
    this.resolvedServicePrice = 0;
    this.apptPayError = "";
    this.tiers = [];
    this.regCredits = [];
    this.buyer = {
      ...this.buyer,
      tierId: "dropin",
      memberCreditProductId: "",
      memberAccessProductId: "",
    };
    // PLAT-6805: a different type/provider means different family access.
    this.familyAccess = {};
    this.familyAccessRequested.clear();
    this.people = this.people.map((p) =>
      p.memberAccessProductId ? { ...p, tierId: "dropin", memberAccessProductId: "" } : p,
    );
  }

  /**
   * PLAT-5624: resolve an anonymous visitor's appointment pay options.
   *
   * Same data the signed-in path gets, minus anything roster-scoped: the real
   * drop-in Stripe product id (minted by this call when it doesn't exist yet),
   * the authoritative price, and the credit packs / memberships that can be
   * bought to cover a credit-priced service. A guest owns no credits, so the
   * options come back as purchasable tiers rather than redeemable ones — which
   * is exactly the "credit options" the credit-priced flow was missing.
   */
  private async loadGuestAppointmentPayContext(seq: number): Promise<void> {
    const po = await fetchAppointmentPaymentOptionsAnon(
      this.orgId,
      this.appointmentProviderUserId,
      this.appointmentTypeId,
      this.startIso || undefined,
    );
    // `loadMember()` resolves concurrently with this. If it landed while we
    // were awaiting, the member path owns the tiers (they include the credits
    // this visitor actually holds) — don't stomp them with the guest view.
    // P1: and a guest load superseded by a NEWER guest load loses too.
    if (this.memberCtx || seq !== this.apptPaySeq) return;
    if (!po) {
      // Fail closed: `apptPayResolved` stays false, so `freeBookingAllowed`
      // refuses a $0 booking and the CTA explains itself instead of quietly
      // giving the service away.
      this.apptPayError =
        "We couldn’t load the price for this appointment. Please refresh and try again.";
      return;
    }
    this.apptPayError = "";
    this.applyAppointmentPayOptions(po);
    const tiers = tiersFromPaymentOptions(po);
    this.tiers = tiers;
    void this.enrichTierNames();
    if (
      tiers.length &&
      (!this.buyer.tierId || this.buyer.tierId === "dropin")
    ) {
      this.buyer = { ...this.buyer, tierId: tiers[0].id };
    }
  }

  /**
   * PLAT-5624: record the authoritative charge target for an appointment.
   *
   * `payment.productId` / `dropInStripeProductId` are resolved server-side and
   * are the only reliable source — the public schedule feed's own
   * `stripeProductId` is empty until this lookup has run at least once.
   */
  private applyAppointmentPayOptions(po: {
    isFree?: boolean | null;
    discountedRateProductId?: string | null;
    payment?: { productId: string; price: number } | null;
    dropInStripeProductId?: string | null;
  }): void {
    this.apptPayResolved = true;
    this.apptTypeIsFree = po.isFree === true && !po.discountedRateProductId;
    this.resolvedServiceProductId =
      po.dropInStripeProductId || po.payment?.productId || "";
    this.resolvedServicePrice =
      typeof po.payment?.price === "number" ? po.payment.price : 0;
  }

  /**
   * Check whether the signed-in member has already completed this activity's
   * waiver/required forms, so we can skip re-asking. No-op without a real
   * activity context or when not signed in.
   */
  private async loadWaiverStatus(): Promise<void> {
    if (
      !this.memberCtx ||
      !this.orgSlug ||
      !this.eventSlug ||
      !this.activityId
    ) {
      return;
    }
    const seq = ++this.waiverStatusSeq;
    const classTarget = this.classTargetKey();
    this.classPayError = "";
    const needsClassPreparation = !this.isAppt && this.classPricingUnresolved;
    this.classPayPending = needsClassPreparation;
    this.ownedAccessProductIds = [];
    this.ownedProductIds = [];
    this.memberDropInPrice = null;
    this.deferredMemberships = undefined;
    this.tiers = this.tiers.filter((t) => t.id !== MEMBER_DROPIN_TIER_ID);
    this.buyer = { ...this.buyer, memberAccessProductId: "" };
    const ctx = await fetchEventRegistrationContext(
      this.orgId,
      this.orgSlug,
      this.eventSlug,
      this.activityId,
      this.memberCtx.userId,
    );
    if (seq !== this.waiverStatusSeq || classTarget !== this.classTargetKey()) return;
    this.classPayPending = false;
    if (!ctx) {
      if (needsClassPreparation || this.classPricingUnresolved) this.classPayError =
        "We couldn’t confirm this class’s payment options. Please try again.";
      return;
    }
    if (!this.isAppt && ctx.paymentOptions) this.applyClassPaymentOptions(ctx.paymentOptions);

    const waiver = ctx.formData.find((f) => f.type === "waiver");
    const onFile = !!waiver?.isComplete && this.hasWaiverRequirement;
    this.waiverOnFile = onFile;
    // W2: this used to clear `waiverChecked` unconditionally before
    // recomputing it. The consent checkbox is live from first paint — the
    // waiver form resolves in ONE round trip while this status needs two
    // (member context, then registration context) — so a member who ticked
    // "I agree" while the request was still in flight had it silently
    // un-ticked, re-blocking Continue with nothing to explain why. Consent is
    // only ever ADDED here: a waiver already on file pre-satisfies the step,
    // and anything else leaves the visitor's own answer alone.
    if (onFile) this.waiverChecked = true;

    // Source the member's activity-scoped credit-packs (customer-app parity).
    this.regCredits = [...ctx.credits, ...(ctx.guestPassCredits ?? [])];
    this.ownedAccessProductIds = ctx.ownedAccessProductIds ?? [];
    this.ownedProductIds = ctx.ownedProductIds ?? [];
    this.memberDropInPrice = ctx.memberDropInPrice ?? null;
    this.deferredMemberships = ctx.deferredMemberships;
    this.applyOwnedMembershipAccess();
    if (!this.isAppt && (this.classPricingUnresolved || (needsClassPreparation && !ctx.paymentOptions))) {
      this.classPayError = "We couldn’t confirm this class’s payment options. Please try again.";
    }

    // Auto-apply the best eligible credit for the buyer, using the same
    // priority the customer app applies (soonest expiry, then fewest left).
    const best = pickBestMemberCredit(ctx.credits);
    if (
      best &&
      !this.buyer.memberCreditProductId &&
      !this.buyer.memberAccessProductId &&
      !this.forSomeoneElse
    ) {
      this.buyer = {
        ...this.buyer,
        memberCreditProductId: best.productId,
        memberCreditPunchCardId: this.isAppt ? undefined : best.punchCardId,
      };
    }
  }

  /**
   * Apply canonical $0 membership access only to the signed-in attendee.
   * This remains separate from member-credit selection, so registration uses
   * `accessedWithProductId` and never consumes a credit for the free tier.
   */
  private applyOwnedMembershipAccess(): void {
    // Tiers are scoped per attendee (`audience`), so this is safe while the
    // member also books for others: only their own seat sees member pricing.
    if (!this.memberCtx) return;
    this.tiers = memberTiers(this.tiers, {
      freeProductIds: this.ownedAccessProductIds,
      ownedProductIds: this.ownedProductIds,
      memberDropInPrice: this.memberDropInPrice,
      deferredMemberships: this.deferredMemberships,
    });
    this.applyFamilyAccess();
    const productId = this.ownedAccessProductIds.find((id) =>
      this.tiers.some((tier) => tier.id === id),
    );
    if (productId) {
      this.buyer = {
        ...this.buyer,
        tierId: productId,
        memberAccessProductId: productId,
        memberCreditProductId: "",
        useCredit: false,
      };
      return;
    }
    // PLAT-6791: a member discount replaces the buyer's drop-in; a membership
    // they already own is never offered back to them.
    const hasMemberDropIn = this.tiers.some(
      (t) => t.id === MEMBER_DROPIN_TIER_ID,
    );
    const current = this.tiers.find((t) => t.id === this.buyer.tierId);
    if (
      (this.buyer.tierId === "dropin" && hasMemberDropIn) ||
      (current && !tierVisibleTo(current, true, this.ownedAccessProductIds))
    ) {
      const first = this.tiersForAttendee(true)[0];
      if (first) this.buyer = { ...this.buyer, tierId: first.id };
    }
  }

  /** PLAT-6791: tiers one attendee may choose (see `PricingTier.audience`). */
  private tiersForAttendee(isBuyer: boolean, familyUserId = 0): PricingTier[] {
    return this.tiers.filter((t) =>
      tierVisibleToAttendee(
        t,
        { isBuyer, familyUserId },
        this.ownedAccessProductIds,
      ),
    );
  }

  /**
   * PLAT-6805: load a family attendee's own payment options, as the Customer
   * Dashboard does when booking for them (registration context with their
   * `targetUserId`), so their own All Access pack or member price applies to
   * their seat. Once per family member per activity.
   */
  private async loadFamilyAccess(familyUserId: number): Promise<void> {
    if (!this.memberCtx || !familyUserId) return;
    if (this.familyAccessRequested.has(familyUserId)) return;
    this.familyAccessRequested.add(familyUserId);
    const appointmentQuoteSequence = this.apptPaySeq;
    let access: FamilyAccess | null = null;
    if (this.isAppt) {
      if (!this.appointmentTypeId || !this.appointmentProviderUserId) return;
      const ctx = await fetchAppointmentRegistrationContext(
        this.orgId,
        this.appointmentProviderUserId,
        this.appointmentTypeId,
        familyUserId,
        this.startIso || undefined,
      );
      if (appointmentQuoteSequence !== this.apptPaySeq) return;
      if (ctx) {
        const own = ownedAccess(ctx.paymentOptions);
        access = {
          ...own,
          memberDropInPrice: own.memberDropInPrice
            ?? (ctx.paymentOptions.discountedRateProductId ? ctx.paymentOptions.payment?.price ?? null : null),
        };
      }
    } else {
      if (!this.orgSlug || !this.eventSlug || !this.activityId) return;
      const ctx = await fetchEventRegistrationContext(
        this.orgId,
        this.orgSlug,
        this.eventSlug,
        this.activityId,
        familyUserId,
      );
      if (ctx) {
        access = {
          freeProductIds: ctx.ownedAccessProductIds ?? [],
          ownedProductIds: ctx.ownedProductIds ?? [],
          memberDropInPrice: ctx.memberDropInPrice ?? null,
          deferredMemberships: ctx.deferredMemberships,
        };
      }
    }
    if (!access) {
      // Retry on the next add; the seat falls back to normal prices meanwhile.
      this.familyAccessRequested.delete(familyUserId);
      return;
    }
    this.familyAccess = { ...this.familyAccess, [familyUserId]: access };
    this.applyFamilyAccess();
  }

  /**
   * PLAT-6805: shape tiers for every family member's own access and move each
   * family attendee onto their own All Access / member price when they hold
   * one. Idempotent; re-run whenever tiers or access change.
   */
  private applyFamilyAccess(): void {
    let tiers = this.tiers;
    for (const [id, access] of Object.entries(this.familyAccess)) {
      tiers = withFamilyAccess(tiers, Number(id), access, this.ownedAccessProductIds);
    }
    this.tiers = tiers;
    let changed = false;
    const people = this.people.map((p) => {
      const access = p.familyUserId ? this.familyAccess[p.familyUserId] : undefined;
      if (!access || p.memberCreditProductId) return p;
      const visible = this.tiersForAttendee(false, p.familyUserId);
      const freeTier = visible.find((t) =>
        familyAccessProductId(t.id, p.familyUserId, access.freeProductIds),
      );
      const current = visible.find((t) => t.id === p.tierId);
      const next = freeTier ?? (current ? null : visible[0]);
      if (!next || next.id === p.tierId) return p;
      changed = true;
      return {
        ...p,
        tierId: next.id,
        memberAccessProductId: familyAccessProductId(
          next.id,
          p.familyUserId,
          access.freeProductIds,
        ),
        useCredit: false,
      };
    });
    if (changed) this.people = people;
  }


  /**
   * Resolve owned require-once responses for classes and appointments.
   * Event waiver status alone does not supply reusable intake/agreement IDs.
   * Always-required forms stay excluded; reusable response IDs still attach
   * to the booking through formsOnFileSubmissions.
   */
  private async loadAppointmentFormsOnFile(): Promise<void> {
    if (!this.memberCtx || !this.orgId) return;
    const ids = this.reusableFormIds();
    if (!ids.length) return;
    const seq = ++this.formsOnFileSeq;
    const onFile = await fetchFormsOnFile(this.orgId, ids);
    if (seq !== this.formsOnFileSeq) return; // superseded by a newer form set
    this.formsOnFile = onFile;
    this.requestUpdate();
    const waiverId = this.classForms.find(
      (f) => f.type === "waiver" && f.requirement !== ALWAYS_REQUIRE,
    )?.formId;
    // W2 again: add-only. This lands after first paint (member context, then
    // this), so flipping it to false would un-tick a consent box the visitor
    // ticked while it was in flight.
    if (waiverId && onFile.has(waiverId) && this.hasWaiverRequirement) {
      this.waiverOnFile = true;
      this.waiverChecked = true;
    }
  }

  /** Declared form ids a previous signature can satisfy ("always require" can't). */
  private reusableFormIds(): number[] {
    return [
      ...new Set(
        this.classForms
          .filter((f) => !!f.formId && f.requirement !== ALWAYS_REQUIRE)
          .map((f) => f.formId),
      ),
    ];
  }

  /**
   * `{ formId, formResponseId }` for the declared forms the member already had
   * on file — nothing to submit, but everything to link.
   */
  private formsOnFileSubmissions(): {
    formId: number;
    formResponseId: string;
  }[] {
    const declared = new Set(this.reusableFormIds());
    const subs: { formId: number; formResponseId: string }[] = [];
    for (const [formId, formResponseId] of this.formsOnFile) {
      if (!declared.has(formId) || !formResponseId) continue;
      subs.push({ formId, formResponseId });
    }
    return subs;
  }

  /**
   * Prior-form ids required by the products currently being CHARGED.
   *
   * Scoped to charged attendees deliberately: a credit-covered or $0 attendee
   * never reaches `validateRequiredForms`, so demanding their product's form
   * would block a booking the server would have accepted. Ids already collected
   * as an activity form are dropped — an org that attaches the same form to
   * both should not make the customer sign it twice.
   */
  private requiredProductFormIds(): number[] {
    const activityIds = new Set(
      [
        ...(this.waiverForm ? [this.waiverForm.id] : []),
        ...(this.termsForm ? [this.termsForm.id] : []),
        ...this.intakeForms.map((f) => f.id),
      ].filter(Boolean),
    );
    const ids = new Set<number>();
    for (const { a } of this.chargedAttendees()) {
      const tier = checkoutMath.findTier(this.tiers, a.tierId);
      const formId = tier?.priorFormId;
      if (formId && !activityIds.has(formId)) ids.add(formId);
    }
    return [...ids];
  }

  /**
   * Fetch the charged products' prior forms, minus any the signed-in customer
   * has already completed.
   *
   * Re-runs whenever the required id set changes, because the buyer can switch
   * pricing tier at any point before paying and each tier can carry a different
   * form. Answers are keyed by form id, so switching away and back keeps what
   * was already typed.
   */
  private async loadProductForms(): Promise<void> {
    const ids = this.requiredProductFormIds();
    const key = `${this.orgId}|${this.signedInName ? "member" : "guest"}|${ids.join(",")}`;
    if (key === this.loadedProductFormKey) return;
    this.loadedProductFormKey = key;
    const seq = ++this.productFormsSeq;
    if (!this.orgId || !ids.length) {
      this.productFormsPending = false;
      this.productForms = [];
      return;
    }
    this.productFormsPending = true;
    this.requestUpdate();
    try {
      // "Require once", honoured properly: a member who signed this form on a
      // previous purchase is not asked again. Guests skip the lookup entirely —
      // a roster that was just created has nothing on file.
      const onFile = this.memberCtx
        ? await fetchFormsOnFile(this.orgId, ids)
        : new Map<number, string | null>();
      const forms = await Promise.all(
        ids
          .filter((id) => !onFile.has(id))
          .map((id) => fetchAppointmentForm(this.orgId, id)),
      );
      if (seq !== this.productFormsSeq) return; // superseded
      // A form that failed to load, or that carries neither questions nor body
      // text, is nothing the customer can complete. Dropping it here matches
      // `loadForms()`; the charge is the real gate either way, so this can only
      // ever fail towards "let the server decide".
      this.productForms = forms.filter(
        (f): f is EmbedForm =>
          !!f && (f.questions.length > 0 || !!f.details?.trim()),
      );
    } catch {
      if (seq !== this.productFormsSeq) return;
      this.productForms = [];
      // Allow a retry on the next selection change.
      this.loadedProductFormKey = "";
    } finally {
      if (seq === this.productFormsSeq) {
        this.productFormsPending = false;
        this.requestUpdate();
      }
    }
  }

  private intakeLoaded = false;

  /**
   * W1: identity of the form set `loadForms()` last committed to resolving.
   * Compared BY VALUE, not by reference — `zipper-schedule` hands over
   * `.classForms=${c.forms ?? []}`, which mints a brand-new array on every
   * parent render, so a reference check would re-arm the loader forever.
   */
  private loadedFormsKey = "";
  /** W1: guards against an older loadForms() response landing after a newer one. */
  private formsSeq = 0;
  /** W2: guards against an older loadWaiverStatus() response landing after a newer one. */
  private waiverStatusSeq = 0;
  /** P1: guards against an older pay-context response landing after a newer one. */
  private apptPaySeq = 0;
  /** Guards against an older forms-on-file response landing after a newer one. */
  private formsOnFileSeq = 0;
  /** Declared form id → the member's existing response id (null when unknown). */
  private formsOnFile = new Map<number, string | null>();

  /** W1: value identity of the currently declared form set + its org. */
  private formsKey(): string {
    return `${this.orgId}|${this.orgSlug}|${this.eventSlug}|${this.activityId}|${this.classForms.map((f) => `${f.formId}:${f.type ?? ""}`).join(",")}`;
  }

  /**
   * W1: drop everything resolved for the PREVIOUS form set and resolve the new
   * one. `formsResolved` goes back to false first, so the waiver gate is closed
   * for the whole window in which the new set is unresolved — a late-arriving
   * waiver blocks Continue exactly like one that was declared up front.
   */
  private rearmForms(): void {
    // Consent and responses belong to one exact form/activity context. Keeping
    // them while the parent swaps classes can let an old checkbox satisfy a
    // new waiver, or attach an old form response to the new registration.
    ++this.waiverStatusSeq;
    this.intakeLoaded = false;
    this.formsError = false;
    this.formsResolved = false;
    this.waiverForm = null;
    this.termsForm = null;
    this.intakeForms = [];
    this.waiverChecked = false;
    this.termsChecked = false;
    this.termsConsents = {};
    this.waiverOnFile = false;
    this.productForms = [];
    this.loadedProductFormKey = "";
    this.intakeAnswers = {};
    this.intakeSubmissions = [];
    this.productSubmissions = [];
    // The roster itself is still valid, but it was minted for the previous
    // context's forms; dropping it here would force a second `create-guest`
    // (which 401s), so it is deliberately kept — only the responses reset.
    this.intakeError = "";
    this.formsOnFile = new Map();
    ++this.formsOnFileSeq;
    void this.loadForms();
    void this.loadWaiverStatus();
    void this.loadAppointmentFormsOnFile();
  }

  /**
   * True once loadForms() has finished resolving every declared class form.
   * Distinct from `intakeLoaded`, which is set on ENTRY purely to de-dupe
   * concurrent calls. The waiver gate needs "finished", not "started": before
   * resolution a null `waiverForm` means "still loading" (keep blocking);
   * after it, null means "no usable form" (don't block).
   */
  @bookingCheckoutActorState.field("formsResolved")
  private formsResolved!: BookingCheckoutActorState["formsResolved"];

  /**
   * 2b: loadForms() rejected (network down, API 5xx). Deliberately does NOT
   * flip `formsResolved` — the waiver gate stays CLOSED, because a waiver we
   * could not load is a waiver nobody has signed. What it does buy is an
   * explanation: without it `stepValid(1)` returns false forever while
   * `step1Errors()` returns nothing, so the visitor stares at a permanently
   * disabled Continue button with no reason and no way out.
   */
  @bookingCheckoutActorState.field("formsError")
  private formsError!: BookingCheckoutActorState["formsError"];

  protected firstUpdated(): void {
    void this.loadForms();
    void this.enrichTierNames();
  }

  /**
   * Resolve each pay-option tier against the org product catalog (mirrors the
   * customer app's `getOrgProductById`) to fill in the real product name AND
   * price — the schedule feed carries a generic "Membership" label, a
   * placeholder price, and a Zipper product id (not a Stripe id). We match by
   * the tier's product id (or stripe id) and also correct stripeProductId to the
   * real Stripe id so the charge targets the right product. Empty catalogs
   * still sanitize unresolved choices instead of exposing placeholders.
   */
  private async enrichTierNames(): Promise<void> {
    const seq = ++this.catalogSeq;
    const orgId = this.orgId;
    const tiers = this.tiers;
    const inputTiers =
      this.catalogTiers === tiers ? this.catalogInputTiers ?? tiers : tiers;
    this.catalogInputTiers = inputTiers;
    this.catalogTiers = undefined;
    if (!orgId || !inputTiers.length) {
      this.catalogRemovedAllChoices = false;
      this.catalogTiers = tiers;
      return;
    }
    const catalog = await fetchProductCatalog(orgId);
    if (seq !== this.catalogSeq || orgId !== this.orgId || tiers !== this.tiers)
      return;
    // A parent replacement also loses the synthetic buyer/guest/family tiers.
    // Restore their scope before joining the authoritative catalog prices.
    let scoped = this.memberCtx
      ? memberTiers(inputTiers, {
          freeProductIds: this.ownedAccessProductIds,
          ownedProductIds: this.ownedProductIds,
          memberDropInPrice: this.memberDropInPrice,
          deferredMemberships: this.deferredMemberships,
        })
      : inputTiers;
    for (const [id, access] of Object.entries(this.familyAccess)) {
      scoped = withFamilyAccess(scoped, Number(id), access, this.ownedAccessProductIds);
    }
    this.tiers = scoped.flatMap((t) => {
      const entry =
        catalog[t.id] ??
        (t.stripeProductId ? catalog[t.stripeProductId] : undefined);
      // Drop-in is not a shop pack. Matching it to a catalog one-time product
      // used to rewrite "This class only" as a duplicate "Class pack" row.
      // PLAT-6805: a family member's own tier keeps its member price; only
      // the name comes from the catalog.
      if (t.forFamilyUserId !== undefined) {
        const own = catalog[t.stripeProductId ?? ""];
        if (own?.name && t.recurring) return [{ ...t, label: own.name }];
        return [
          t.recurring && t.price === 0 && (!t.label || t.label === "Membership")
            ? { ...t, label: "Your membership", sub: "No additional charge" }
            : t,
        ];
      }
      if (t.id === "dropin" || t.id === MEMBER_DROPIN_TIER_ID) {
        if (!entry) return [t];
        return [
          {
            ...t,
            stripeProductId: entry.stripeProductId || t.stripeProductId,
            priorFormId: entry.priorFormId ?? t.priorFormId,
          },
        ];
      }
      if (!entry) {
        // Archived/hidden products are omitted from the catalog, so an unpriced
        // subscriptionPrices placeholder would render as "Membership" / `$`.
        if (typeof t.price !== "number" || !Number.isFinite(t.price)) {
          return [];
        }
        // PLAT-6791: a $0 membership tier (All Access) the shop can't price
        // is only usable by someone who already owns it — never a free
        // "purchase" for a guest, which the server would reject anyway.
        if (t.price === 0 && t.recurring) {
          if (t.id.startsWith(BUY_PREFIX)) return [];
          // Preserve an already priced purchase copy, but prevent the final
          // memberTiers pass from inventing a free buy: copy when unpriced.
          const hasPricedBuyOption = scoped.some((option) =>
            option.id === `${BUY_PREFIX}${t.id}` &&
            Number.isFinite(option.price) && option.price > 0 &&
            !!option.label && option.label !== "Membership",
          );
          return [{
            ...t,
            audience: hasPricedBuyOption ? t.audience ?? "owner" : "owner",
            ...(!t.label || t.label === "Membership"
              ? { label: "Your membership", sub: "No additional charge" }
              : {}),
          }];
        }
        if (t.recurring && (!t.label || t.label === "Membership")) return [];
        return [t];
      }
      // `subscriptionPrices` is a legacy/misnamed payment-option bucket that
      // can also contain one-time credit packs. The product catalog is the
      // first place this checkout sees the authoritative classification, so
      // correct the customer-facing billing promise at this join boundary.
      const isOneTimePack =
        entry.category === "packs" || entry.cadence === "one-time";
      const price = this.ownedAccessProductIds.includes(t.id)
        ? 0
        : Number.isFinite(entry.price)
          ? entry.price
          : t.price;
      if (typeof price !== "number" || !Number.isFinite(price)) return [];
      return [
        {
          ...t,
          label: entry.name || t.label,
          // PLAT-6856: a recurring product must not keep the generic
          // "Class pack" copy from the punch-card bucket.
          sub: isOneTimePack
            ? "Class pack"
            : t.sub === "Class pack"
              ? "Recurring"
              : t.sub,
          recurring: isOneTimePack ? false : t.recurring,
          // Catalog is the source of truth for product price; keep the tier's own
          // price only when the catalog has none (e.g. the drop-in, which isn't a
          // catalog product). PLAT-7018: a free product's catalog price is 0, and
          // upsell tiers arrive unpriced, so `||` rendered "$NaN".
          price,
          stripeProductId: entry.stripeProductId || t.stripeProductId,
          // The form the CHARGE will demand for this product — see
          // `loadProductForms`. Resolved here because this is already the one
          // place a tier is matched against the catalog.
          priorFormId: entry.priorFormId ?? t.priorFormId,
        },
      ];
    });
    this.catalogRemovedAllChoices = this.tiers.length === 0;
    this.applyOwnedMembershipAccess();
    this.catalogTiers = this.tiers;
    void this.loadProductForms();
  }

  /**
   * Load the class's real forms: the waiver (rendered + submitted unless already
   * on file) and any non-waiver intake forms (guests only). The backend requires
   * a real form RESPONSE for required forms — a checkbox alone fails register.
   */
  private async loadForms(): Promise<void> {
    if (this.intakeLoaded) return;
    this.intakeLoaded = true;
    this.loadedFormsKey = this.formsKey();
    const seq = ++this.formsSeq;
    if (!this.classForms.length) {
      // Genuinely nothing declared ⇒ nothing to resolve, so the waiver gate has
      // nothing to wait for. `willUpdate` re-arms this if forms show up later.
      this.formsResolved = true;
      return;
    }
    if (!this.orgId) {
      // W1 fail CLOSED: forms ARE declared — we just have no org to fetch them
      // from. This used to share the branch above and flip `formsResolved`,
      // which is precisely what let `stepValid(1)`'s C1 fallback wave a booking
      // through with a required waiver neither rendered nor signed. Treat it as
      // a load failure instead: `formsError` explains it and offers the retry
      // (2b), `intakeLoaded`/`loadedFormsKey` reset so a late `orgId` re-arms.
      this.intakeLoaded = false;
      this.loadedFormsKey = "";
      this.formsError = true;
      return;
    }
    try {
      const defs = await Promise.all(
        this.classForms.map(async (f) => ({
          type: f.type,
          form: await fetchAppointmentForm(this.orgId, f.formId),
        })),
      );
      if (seq !== this.formsSeq) return; // superseded by a newer form set
      // `fetchAppointmentForm` is a resilient live adapter: network/API
      // failures are caught there and returned as null. Treating null as an
      // empty form would filter it out below, mark the set resolved and reopen
      // the waiver gate. A declared form that could not be loaded must fail
      // closed exactly like a thrown request.
      if (defs.some((d) => !d.form)) {
        throw new Error("A declared form could not be loaded");
      }
      const valid = defs.filter(
        (d): d is { type: string; form: EmbedForm } =>
          !!d.form && (d.form.questions.length > 0 || !!d.form.details?.trim()),
      );
      this.waiverForm = valid.find((d) => d.type === "waiver")?.form ?? null;
      this.termsForm =
        valid.find((d) => d.type === "cancelation-agreement")?.form ?? null;
      this.intakeForms = valid
        .filter((d) => d.type !== "waiver")
        .map((d) => d.form);
      this.termsOpen = this.cancellationForms().some((f) =>
        this.formNeedsResponse(f.id),
      );
      this.formsError = false;
      this.formsResolved = true;
    } catch {
      if (seq !== this.formsSeq) return; // superseded by a newer form set
      // 2b: fail CLOSED. `formsResolved` stays false so stepValid(1) keeps
      // Continue disabled and nobody books past a required waiver we never
      // managed to fetch. Surface WHY instead of dead-ending silently, and
      // re-arm the de-dupe latch so retryForms() can have another go.
      this.formsError = true;
      this.intakeLoaded = false;
    }
  }

  /** 2b: retry affordance for the error banner rendered on step 1. */
  private retryForms(): void {
    if (!this.formsError) return;
    this.formsError = false;
    void this.loadForms();
  }

  /** A session alone does not satisfy a declared form requirement. */
  private formNeedsResponse(formId: number): boolean {
    return (
      this.classForms.some(
        (f) => f.formId === formId && f.requirement === ALWAYS_REQUIRE,
      ) ||
      !this.memberCtx ||
      !this.formsOnFile.get(formId)
    );
  }

  private requiredIntakeForms(): EmbedForm[] {
    const cancellationIds = new Set(this.cancellationForms().map((f) => f.id));
    return [...new Map(this.intakeForms.map((f) => [f.id, f])).values()].filter(
      (f) =>
        (this.buyerAttends || cancellationIds.has(f.id)) &&
        this.formNeedsResponse(f.id),
    );
  }

  private cancellationForms(): EmbedForm[] {
    const ids = new Set(
      this.classForms
        .filter((f) => f.type === "cancelation-agreement")
        .map((f) => f.formId),
    );
    return [...new Map(this.intakeForms.map((f) => [f.id, f])).values()].filter(
      (f) => ids.has(f.id),
    );
  }

  private cancellationComplete(f: EmbedForm): boolean {
    return (
      !this.formNeedsResponse(f.id) ||
      (f.questions.length
        ? f.questions.every((q) => !this.questionError(f.id, q))
        : !!this.termsConsents[f.id] ||
          (this.cancellationForms().length === 1 && this.termsChecked))
    );
  }

  private get termsValid(): boolean {
    return this.cancellationForms().every((f) => this.cancellationComplete(f));
  }

  /** Other pending intake belongs in Your details; agreements belong in Payment. */
  private answerableForms(): EmbedForm[] {
    const cancellationIds = new Set(this.cancellationForms().map((f) => f.id));
    return [
      ...this.requiredIntakeForms().filter((f) => !cancellationIds.has(f.id)),
      ...this.productForms,
    ];
  }

  private renderIntakeForms(): TemplateResult {
    const forms = this.answerableForms();
    if (!forms.length) return html``;
    return html`
      ${forms.map(
        (f) =>
          html`<div class="person">
            <div class="ph"><h5>${f.name}</h5></div>
            ${f.questions.map((q) => this.renderIntakeField(f.id, q))}
          </div>`,
      )}
      ${
        this.intakeError
          ? html`<div class="err">${this.intakeError}</div>`
          : nothing
      }
    `;
  }

  /** Questions a person's own contact fields answer, so the form never re-asks. */
  private isIdentityQuestion(q: FormQuestion): boolean {
    return (
      q.type === "first-name" ||
      q.type === "last-name" ||
      q.type === "email" ||
      q.type === "phone-number"
    );
  }

  /**
   * Value for a form question that just re-asks something the person's own
   * contact fields already collected (first/last name, email, phone) — so
   * that field can be hidden from the waiver/intake form and auto-answered
   * instead of asked a second time. Returns null when the question isn't one
   * of these (must be asked normally), including when the matching field
   * hasn't actually been filled in yet (e.g. phone isn't required in step 1,
   * so a required phone waiver question still needs asking).
   *
   * `person` selects whose fields answer it: omitted for the buyer's own
   * copy of a form, or an extra attendee for theirs (see
   * `attendeeRequiredForms`).
   */
  private identityAnswerFor(q: FormQuestion, person?: Attendee): string | null {
    const src = person ?? this.buyer;
    const val =
      q.type === "first-name"
        ? src.firstName
        : q.type === "last-name"
          ? src.lastName
          : q.type === "email"
            ? src.email
            : q.type === "phone-number"
              ? src.phone
              : "";
    return val ? val : null;
  }

  /**
   * Where a typed answer lives in `intakeAnswers`.
   *
   * Every extra attendee answers the SAME form on their own behalf, so their
   * answers are namespaced by their position in `people` — without that they
   * would all write over one another's (and over the buyer's).
   */
  private answerKey(
    formId: number,
    q: FormQuestion,
    personIndex?: number,
  ): string {
    const scope = personIndex === undefined ? "" : `p${personIndex}:`;
    return `${scope}${formId}:${q.key}`;
  }

  /**
   * Effective, trimmed answer for a form question during BOTH gating and
   * submission: the identity value auto-filled from the person's contact
   * fields (for the identity questions hidden from the form) OR the typed
   * intake answer. Mirrors the exact resolution `bookClass()` and
   * `formsForAttendee()` submit with — so a REQUIRED identity question
   * (first/last name, email, phone) never permanently blocks the Continue gate
   * just because its value lives on the contact fields and was never written
   * into `intakeAnswers` (the bug that stalled waiver checkout).
   */
  private answerFor(
    formId: number,
    q: FormQuestion,
    person?: { index: number; a: Attendee },
  ): string {
    return (
      this.identityAnswerFor(q, person?.a) ??
      this.intakeAnswers[this.answerKey(formId, q, person?.index)] ??
      ""
    ).trim();
  }

  /**
   * Signature / free-text questions on a GUEST's copy of a form.
   *
   * Only the guest path uses this (see {@link formsForAttendee}): guests are
   * not present in the checkout to answer anything, they sign at check-in, so
   * their placeholder response records consent rather than a real answer.
   *
   * The BOOKER's own waiver deliberately no longer goes through here. It used
   * to: ticking the consent checkbox auto-answered every signature/textarea
   * question with the buyer's name, which turned a real form into a rubber
   * stamp — a required "List any medical conditions" textarea was submitted as
   * "Ada Lovelace" and the visitor was never shown it. Those questions are now
   * rendered and answered for real.
   */
  private isConsentQuestion(q: FormQuestion): boolean {
    return q.type === "signature" || q.type === "textarea";
  }

  /**
   * A multi-select answer is stored the way the rest of the platform stores it
   * — a JSON array of the chosen option values. `AthleteFormView` writes
   * `JSON.stringify(selections)` and `formatAnswer` (shared-ux form-utils)
   * `JSON.parse`s it back for the provider's exports and reports, so a bare
   * option string written by the embed would come back as an empty cell.
   * A non-JSON value is tolerated on read as a single selection.
   */
  private multiSelectValues(raw: string): string[] {
    if (!raw) return [];
    try {
      const parsed: unknown = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.filter((v): v is string => typeof v === "string");
      }
    } catch {
      // Not JSON — treat it as one selection rather than losing the answer.
    }
    return [raw];
  }

  /**
   * Per-question validity, and the single source of truth for BOTH the
   * Continue gate and the inline message under the field.
   *
   * Mirrors `validateAnswer` in libs/shared-ux/src/utilities/form-utils.tsx
   * verbatim — same rules, same wording — so a form that the dashboard
   * considers complete is complete here too, and vice versa. Identity
   * questions resolve through `answerFor` against the buyer step, so a
   * required first-name question auto-answered in step 1 is never an error.
   *
   * @returns the message to show, or null when the answer is acceptable.
   */
  private questionError(
    formId: number,
    q: FormQuestion,
    person?: { index: number; a: Attendee },
  ): string | null {
    if (!q.required) return null;
    const v = this.answerFor(formId, q, person);
    if (q.type === "multi-select") {
      return this.multiSelectValues(v).length
        ? null
        : "At least one item must be selected.";
    }
    if (q.type === "single-select") {
      return v ? null : "One item must be selected.";
    }
    if (q.type === "email") {
      return checkoutMath.isValidEmail(v)
        ? null
        : "An email address is required.";
    }
    if (q.type === "date") return v ? null : "A date must be selected.";
    return v ? null : "This field is required.";
  }

  /**
   * The value actually PUT to `/form/:orgId/:formId/responses-anonymous`.
   *
   * Identical to the gated answer except for dates: the control holds the
   * native `<input type="date">` value (YYYY-MM-DD) so it round-trips, but the
   * platform stores a date answer as an ISO instant — `getDefaultValueForQuestion`
   * seeds `new Date().toISOString()` and `formatAnswer` renders through
   * `buildDateString` → `displayUtcAsLocal`. Midnight is resolved in the
   * booker's own timezone (the no-Z form parses as local) so the date they
   * picked is the date that renders back.
   */
  private submitValueFor(
    formId: number,
    q: FormQuestion,
    person?: { index: number; a: Attendee },
  ): string {
    const v = this.answerFor(formId, q, person);
    if (q.type !== "date" || !/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
    const local = new Date(`${v}T00:00:00`);
    return Number.isNaN(local.getTime()) ? v : local.toISOString();
  }

  /**
   * Form-question keys the visitor has blurred. Per-field errors stay quiet
   * until a field has actually been visited (or Continue has been pressed and
   * set `step1Touched`) — mirrors the dashboard's blurred-question tracking so
   * an untouched form is not a wall of red.
   */
  @bookingCheckoutActorState.field("touchedFields")
  private touchedFields!: BookingCheckoutActorState["touchedFields"];

  private touchField(key: string): void {
    if (this.touchedFields.has(key)) return;
    this.touchedFields = new Set([...this.touchedFields, key]);
  }

  /**
   * Render ONE form question with the control its type calls for.
   *
   * This is the generic form renderer the waiver and the intake forms share.
   * Type coverage matches `FormQuestion.type` and the dashboard renderer
   * (AthleteFormView): text / email / first-name / last-name / phone-number /
   * date / textarea / dropdown / single-select / multi-select / signature.
   *
   * Identity questions the contact fields already collected are dropped here
   * and auto-answered (see `identityAnswerFor`) so nobody is asked for their
   * name twice. `person` renders an extra attendee's copy of the question
   * instead of the buyer's.
   */
  private renderIntakeField(
    formId: number,
    question: FormQuestion,
    person?: { index: number; a: Attendee },
  ): TemplateResult | typeof nothing {
    if (this.identityAnswerFor(question, person?.a) !== null) return nothing;
    const key = this.answerKey(formId, question, person?.index);
    const error = this.questionError(formId, question, person);
    return renderIntakeQuestion(question, {
      value: this.intakeAnswers[key] ?? "",
      error,
      showError: !!error && (this.touchedFields.has(key) || this.step1Touched),
      onWrite: (value) => {
        this.intakeAnswers = { ...this.intakeAnswers, [key]: value };
      },
      onTouch: () => this.touchField(key),
      parseMultiSelect: (value) => this.multiSelectValues(value),
    });
  }

  /**
   * The forms an EXTRA attendee's own response has to satisfy: the activity's
   * waiver (when it requires one) plus its intake forms. Same list
   * `formsForAttendee()` files after the charge — the point of computing it
   * here too is that the questions get ASKED first.
   *
   * They are asked whether or not the buyer is signed in: a member's own
   * account satisfies their own copy of these forms (see `answerableForms`),
   * but it says nothing about the person they are bringing.
   */
  private attendeeRequiredForms(): EmbedForm[] {
    return [
      ...(this.waiverForm && this.hasWaiverRequirement
        ? [this.waiverForm]
        : []),
      ...this.intakeForms,
    ];
  }

  /**
   * The questions an extra attendee is actually asked, per form.
   *
   * Only REQUIRED questions: the server rejects a response that leaves one
   * blank (`MISSING_REQUIRED_ANSWERS`, and then again at registration through
   * `isFormResponseValid`), while an optional question is nobody's business to
   * answer on someone else's behalf. Identity questions are answered by the
   * attendee's own contact fields, and signature/free-text ones are the
   * consent this checkout records for them (see `isConsentQuestion`), so
   * neither is re-asked here.
   */
  private attendeeAskedQuestions(form: EmbedForm, a: Attendee): FormQuestion[] {
    return form.questions.filter(
      (q) =>
        q.required &&
        !this.isIdentityQuestion(q) &&
        !this.isConsentQuestion(q) &&
        this.identityAnswerFor(q, a) === null,
    );
  }

  /**
   * Whether an extra attendee's contact field has to be filled in because one
   * of THEIR forms requires it. The card asks for email and phone as optional
   * — right up until a required question on the waiver/intake form is the
   * thing that consumes them, at which point leaving them blank produces a
   * response the API refuses.
   */
  private attendeeFieldRequired(
    type: "email" | "phone-number",
  ): boolean {
    return this.attendeeRequiredForms().some((f) =>
      f.questions.some((q) => q.required && q.type === type),
    );
  }

  /** Every unanswered required question across an extra attendee's forms. */
  private attendeeFormErrors(a: Attendee, index: number): string[] {
    const errs: string[] = [];
    for (const f of this.attendeeRequiredForms()) {
      for (const q of f.questions) {
        if (this.isConsentQuestion(q)) continue;
        const err = this.questionError(f.id, q, { index, a });
        if (err) errs.push(`${q.label}: ${err}`);
      }
    }
    return errs;
  }

  /**
   * The extra attendee's own required questions, rendered inside their card.
   *
   * Without these the booker could only ever supply the four identity fields,
   * so any other required question on the activity's waiver/intake form was
   * submitted blank — rejected by the API outright, and rejected again by
   * registration's own `isFormResponseValid` check. Both of those happen AFTER
   * the charge, so the money moved and the seat didn't.
   */
  private renderAttendeeForms(
    person: Attendee,
    index: number,
  ): TemplateResult | typeof nothing {
    const blocks = this.attendeeRequiredForms()
      .map((f) => ({
        form: f,
        questions: this.attendeeAskedQuestions(f, person),
      }))
      .filter((b) => b.questions.length);
    if (!blocks.length) return nothing;
    return html`${blocks.map(
      (b) =>
        html`<div class="attendee-form">
          ${b.questions.map((q) =>
            this.renderIntakeField(b.form.id, q, { index, a: person }),
          )}
        </div>`,
    )}`;
  }

  /**
   * Submit the required forms (once) — the waiver (unless already on file) plus
   * guest intake forms — then open payment. The resulting formResponse ids
   * attach to registration so the backend's "required form" check passes.
   */
  /**
   * PLAT-5624: guarded wrapper for the "Book & pay" CTA.
   *
   * The slot is claimed synchronously so the second half of a double-click is
   * a no-op — previously both clicks ran the whole handler, which submitted
   * the waiver/intake forms twice AND fired two charges (or, on the free/credit
   * path, two registrations), because nothing flipped until after the first
   * `await`.
   */
  private async bookClass(): Promise<void> {
    if (this.chargeCompleted || !this.submission.claim()) return;
    let charged = false;
    try {
      charged = await this.runBooking();
    } finally {
      this.submission.release(charged, this.bookingError || this.intakeError);
    }
  }

  /**
   * Create (once) the guest roster that this checkout's form responses and
   * charge belong to.
   *
   * ORDER MATTERS. The intake/waiver submit is anonymous, so the only way the
   * response can name its author is to already have a roster to cite. Before
   * this, `create-guest` ran inside the charge — strictly after the forms had
   * been filed — so every embed signature landed in Forms with `userId: null`:
   * unattached to the customer, invisible to the "require once" check (the
   * waiver was re-demanded on every booking) and to the roster's own
   * "waiver not signed" indicator.
   *
   * Memoized because `create-guest` is find-or-401, not find-or-reuse — see
   * {@link GuestRosterHandle}. The handle is reused by the payment step and by
   * post-charge registration instead of each minting its own.
   *
   * @returns the handle, or null when the identity could not be established
   *   (the caller must not submit forms or charge in that case).
   */
  private async ensureGuestRoster(): Promise<GuestRosterHandle | null> {
    if (this.guestRoster) return this.guestRoster;
    if (!this.orgId) return null;
    try {
      // The payment step mints its own guest roster as soon as it mounts (to
      // quote tax and fees). `create-guest` is find-or-401, so asking again
      // for the same email here would be refused as "account exists" and send
      // a first-time guest to a sign-in they cannot complete.
      const fromPayment = this.paymentEl
        ? (this.paymentEl.guestRoster ??
          (await this.paymentEl.ensureGuestRoster()))
        : undefined;
      if (fromPayment) {
        this.guestRoster = fromPayment;
        return fromPayment;
      }
      this.guestRoster = await createGuestRoster(
        this.orgId,
        {
          firstName: this.buyer.firstName.trim(),
          lastName: this.buyer.lastName.trim(),
          email: this.buyer.email.trim(),
          phone: this.buyer.phone.trim() || undefined,
          // PLAT-4456: this call is the one the API validates the org's
          // required-signup-fields policy against, and the roster it mints is
          // the customer profile the org sees afterwards.
          ...requiredProfilePayload(this.requiredProfile),
        },
        ...runtimeApiArguments(this.runtime),
      );
      // The payment step may already be mounted (setActiveStep(4) opens it
      // before the CTA runs), so push the handle at it as well as setting it in
      // openPayment() — otherwise that element still tries to create its own.
      if (this.paymentEl) this.paymentEl.guestRoster = this.guestRoster;
      return this.guestRoster;
    } catch (e) {
      // Surfaced here now rather than at the card step, because that is where
      // identity is established. Same remedy as <zipper-payment>: this email is
      // a member, so the anonymous path is closed and sign-in is the way
      // through. Nothing has been submitted or charged at this point.
      if (isAccountExistsError(e)) {
        this.intakeError = accountExistsMessage(this.buyer.email, "book");
        this.openSignInModal();
        return null;
      }
      this.intakeError =
        e instanceof Error && e.message
          ? e.message
          : "Could not save your details — please try again.";
      return null;
    }
  }

  /**
   * The guest roster for a non-buyer attendee, created before their forms are
   * submitted for the same reason as the buyer's — see {@link ensureGuestRoster}.
   *
   * Keyed by email so two attendees sharing the buyer's address don't trigger a
   * second (401-ing) `create-guest`. Returns null when identity can't be
   * established; the caller decides whether that's fatal.
   */
  private async ensureAttendeeRoster(
    a: Attendee,
  ): Promise<GuestRosterHandle | null> {
    const email = (a.email || this.buyer.email).trim();
    if (!this.orgId || !email) return null;
    const cached = this.attendeeRosters.get(email.toLowerCase());
    if (cached) return cached;
    if (email.toLowerCase() === this.buyer.email.trim().toLowerCase()) {
      return this.guestRoster;
    }
    try {
      const handle = await createGuestRoster(
        this.orgId,
        {
          firstName: a.firstName.trim(),
          lastName: a.lastName.trim(),
          email,
          // A co-attendee, not the person checking out — the org's
          // required-signup-fields policy is asked of the buyer only, and this
          // form never collects it for them.
          isPrimaryCustomer: false,
        },
        ...runtimeApiArguments(this.runtime),
      );
      this.attendeeRosters.set(email.toLowerCase(), handle);
      return handle;
    } catch {
      // An existing member booked as a guest lands here. Their response stays
      // unlinked, exactly as before this change — degraded, not broken, and
      // the backend still gets a shot at matching them by email.
      return null;
    }
  }

  /** @returns true only when money moved / the booking was actually placed. */
  private async runBooking(): Promise<boolean> {
    if (!(await this.prepareBooking())) return false;
    return this.placeBooking();
  }

  /**
   * Everything that has to be true before money may move: the steps are
   * valid, and the waiver/intake/product forms are filed against the booker.
   *
   * Shared by the "Book & pay" CTA and the payment element's `beforeCharge`.
   * The Apple/Google Pay sheet charges from Stripe's own button and never
   * goes through the CTA, so without that hook a first-time guest's waiver
   * was never submitted: the charge succeeded and register then refused the
   * booking with "Missing required form" (PLAT-7127).
   *
   * @returns false when the booking must not be charged; the reason is
   *   already on `bookingError` / `intakeError`.
   */
  private async prepareBooking(): Promise<boolean> {
    if (this.classPricingUnresolved) return false;
    if (!this.stepValid(1)) {
      this.step1Touched = true;
      // Bailing silently here is a dead button. This CTA lives on the payment
      // step, and step 1's body — the error list AND the form the errors name —
      // is only in the DOM while step 1 is open (see renderStep). Step 1 can go
      // from valid to invalid after the buyer has left it: a product's prior
      // form is fetched when the tier is chosen (loadProductForms runs on every
      // update), so it lands on a step 1 that is already collapsed and ticked
      // done. Send them back to the questions instead of eating the click.
      if (this.activeStep !== 1) this.setActiveStep(1);
      return false;
    }
    if (!this.termsValid) {
      this.termsOpen = true;
      this.bookingError =
        "Complete the required cancellation form before booking.";
      if (this.activeStep !== 4) this.setActiveStep(4);
      return false;
    }
    // PLAT-6800: never charge a tier the visitor didn't choose.
    if (this.payChoicePending) {
      if (this.activeStep !== 3) this.setActiveStep(3);
      return false;
    }
    // An appointment without intake forms can be free, so neither form submit
    // nor payment creates its guest roster. Establish it before booking and
    // carry its user id into the appointment record.
    if (this.isAppt && !this.memberCtx && !(await this.ensureGuestRoster())) {
      this.setActiveStep(1);
      return false;
    }
    const toSubmit: EmbedForm[] = [];
    // Signed once already, so nothing is written — but the booking still has to
    // name the response it is standing on, or the appointment records no form
    // at all. In-app keeps this link by re-using `defaultResponse.id`; the
    // embed gets the same id from forms-on-file.
    const onFileSubs = this.formsOnFileSubmissions();
    // C3: when the booker already has this waiver on file, submit nothing —
    // `registerAfterCharge()` sends `omitBookerWaiver`, and register-self /
    // register-family-member now accept it and verify server-side that a
    // valid submitted response really exists before skipping the form.
    // Re-submitting here would write a duplicate form-response row on every
    // booking. `renderWaiver()` still shows the real consent block, and this
    // still submits, for anyone who has not signed yet.
    if (
      this.buyerAttends &&
      this.hasWaiverRequirement &&
      this.waiverForm &&
      !this.waiverOnFile
    ) {
      toSubmit.push(this.waiverForm);
    }
    toSubmit.push(...this.requiredIntakeForms());
    // The charged products' prior forms, for members as well as guests — the
    // charge validates these per-buyer regardless of sign-in, and any the
    // member already had on file never made it into `productForms`.
    toSubmit.push(...this.productForms);

    if (toSubmit.length) {
      this.intakeError = "";
      // Identity first. A signed-in customer writes under their session; a
      // first-time booker gets their guest roster created HERE, before a single
      // form is filed, so every response below can name the person it belongs
      // to. See ensureGuestRoster().
      let identity: FormSubmitIdentity | undefined;
      if (this.memberCtx) {
        identity = { kind: "member" };
      } else {
        const roster = await this.ensureGuestRoster();
        // Fail closed: submitting anyway would recreate the exact orphaned
        // responses this ordering exists to prevent, and ensureGuestRoster has
        // already explained why (existing account / API failure).
        if (!roster) return false;
        identity = {
          kind: "guest",
          rosterId: roster.rosterId,
          checkoutToken: roster.checkoutToken,
        };
      }
      const productFormIds = new Set(this.productForms.map((f) => f.id));
      const subs: { formId: number; formResponseId: string }[] = [];
      const productSubs: { formId: number; formResponseId: string }[] = [];
      for (const f of new Map(
        toSubmit.map((form) => [form.id, form]),
      ).values()) {
        const answers = f.questions
          .map((q) => ({
            questionKey: q.key,
            // Every question on the form, answered for real. Identity fields
            // hidden from the form (see renderIntakeField) still need their
            // value submitted — pulled from the buyer step. Same resolution
            // the step gate uses, plus the date normalization the platform
            // stores (see submitValueFor).
            value: this.submitValueFor(f.id, q),
          }))
          .filter((a) => a.value);
        const responseKey = JSON.stringify([
          this.orgId,
          this.memberCtx?.rosterId ?? this.guestRoster?.rosterId,
          f.id,
          answers,
        ]);
        let response = this.savedFormResponses.get(responseKey);
        if (!response) {
          response = submitAppointmentForm(this.orgId, f.id, answers, identity)
            .then((id) => {
              if (!id) this.savedFormResponses.delete(responseKey);
              return id;
            })
            .catch((error: unknown) => {
              this.savedFormResponses.delete(responseKey);
              throw error;
            });
          this.savedFormResponses.set(responseKey, response);
        }
        const id = await response;
        if (!id) {
          this.intakeError = "Could not save your details — please try again.";
          this.bookingError = this.intakeError;
          return false;
        }
        // Routed to the consumer that actually needs it: activity forms go to
        // register, product forms go on the charge line. See productSubmissions.
        (productFormIds.has(f.id) ? productSubs : subs).push({
          formId: f.id,
          formResponseId: id,
        });
      }
      this.intakeSubmissions = [...onFileSubs, ...subs];
      this.productSubmissions = productSubs;
    } else if (onFileSubs.length) {
      this.intakeSubmissions = onFileSubs;
    }
    return true;
  }

  /** The charge / registration half of {@link runBooking}. */
  private async placeBooking(): Promise<boolean> {
    const deferred = this.deferredSelection;
    if (deferred && this.attendees().length !== 1) {
      this.bookingError = 'Book one attendee at a time with a deferred membership.'; return false;
    }
    if (deferred?.tier?.deferredMembershipPurchaseId) {
      try {
        if (!this.deferredBookingAccess || this.preparedDeferredKey !== this.pendingDeferredKey) { this.existingDeferredConsent = false; await this.prepareMembershipBooking(deferred.tier.deferredMembershipPurchaseId); this.requestUpdate(); return false; }
        if (!this.existingDeferredConsent) { this.bookingError = 'Confirm the membership payment timing before booking.'; return false; }
        await this.finishMembershipBooking(); await this.syncMemberPhone(); await this.placeOrder(); return this.placed;
      } catch (e) { this.bookingError = e instanceof Error ? e.message : 'Membership reserved; booking could not be completed. Please try again.'; return false; }
    }
    const needsPayment = this.chargeItems().length > 0 && (this.total > 0 || !!deferred);
    const creditCovered =
      checkoutMath.creditCoveredAttendees(this.attendees()).length > 0;

    if (needsPayment) {
      if (!this.paymentEl) {
        this.openPayment();
        // Mounting the payment element does not charge — release so the buyer
        // can press the CTA again to actually pay.
        return false;
      }
      // Awaited (was fire-and-forget) so the actor state — and the CTA's pending
      // state — cover the whole charge rather than only the dispatch.
      // `prepareBooking()` has just run, so tell `beforeCharge` not to file
      // the same forms a second time.
      this.bookingPrepared = true;
      try {
        await this.paymentEl.submitPayment();
      } finally {
        this.bookingPrepared = false;
      }
      return this.chargeCompleted;
    }

    if (creditCovered || this.total <= 0) {
      // PLAT-5624 fail-closed: getting here with a $0 total means either the
      // org really did configure a free booking, or a pricing lookup failed and
      // we are one line away from giving a paid appointment away. Refuse unless
      // something authoritative says it is free — see `freeBookingAllowed`.
      if (!this.freeBookingAllowed) {
        this.bookingError =
          this.apptPayError ||
          "We couldn’t confirm the price for this appointment. Please refresh and try again, or contact the studio to book.";
        // Re-resolve in the background so a transient failure self-heals.
        void this.loadAppointmentPayContext();
        return false;
      }
      this.bookingError = "";
      try {
        await this.registerAfterCharge([]);
        await this.syncMemberPhone();
        // PLAT-5624: registrations were created — this booking is done even
        // though no money moved, so the actor must stay terminal.
        await this.placeOrder();
        return this.placed;
      } catch (e) {
        this.bookingError =
          e instanceof Error ? e.message : "Could not complete booking.";
        return false;
      }
    }

    if (this.chargeItems().length > 0 && this.total > 0) {
      this.openPayment();
    }
    return false;
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.teardownPayment();
    // Release any active holds so abandoned checkouts free spots immediately.
    for (const { a } of this.attendees()) {
      if (a.holdId) void releaseSpotHold(this.orgId, a.holdId, this.holderKey);
    }
  }

  /** Hold + assign a real spot to the active attendee (live seat map). */
  private async selectRealSpot(spot: SpotAvailability): Promise<void> {
    const list = this.attendees();
    const target = list[this.activeSpot];
    if (!target) return;
    const patch = (p: Partial<Attendee>) => this.patchAttendee(target, p);
    // Release the attendee's previous hold before taking a new one.
    if (target.a.holdId) {
      void releaseSpotHold(this.orgId, target.a.holdId, this.holderKey);
    }
    try {
      const { holdId } = await createSpotHold(this.orgId, {
        spotId: spot.spotId,
        startsAt: this.startIso,
        endsAt: this.endIso,
        holderKey: this.holderKey,
      });
      this.seatError = null;
      patch({ seat: spot.label, spotId: spot.spotId, holdId });
      const next = list.findIndex(({ a }, i) => i > this.activeSpot && !a.seat);
      this.activeSpot = next === -1 ? this.activeSpot : next;
    } catch {
      this.seatError = `Spot ${spot.label} was just taken — please pick another.`;
    }
    // Refresh availability so newly held/taken spots reflect.
    void this.loadSeatMap();
  }

  private patchAttendee(
    target: { a: Attendee; isBuyer: boolean },
    patch: Partial<Attendee>,
  ): void {
    if (target.isBuyer) this.buyer = { ...this.buyer, ...patch };
    else {
      const idx = this.extras.indexOf(target.a);
      this.people = this.people.map((p, i) =>
        i === idx ? { ...p, ...patch } : p,
      );
    }
  }

  private async loadSeatMap(): Promise<void> {
    try {
      this.roomAvail = await fetchSpotAvailability(
        this.orgId,
        this.roomId,
        this.startIso,
        this.endIso,
      );
    } catch {
      this.roomAvail = null; // fall back to the generic grid
    }
  }

  @bookingCheckoutActorState.field("activeStep")
  private activeStep!: BookingCheckoutActorState["activeStep"];

  @bookingCheckoutActorState.field("done")
  private done!: BookingCheckoutActorState["done"];

  @bookingCheckoutActorState.field("buyer")
  private buyer!: BookingCheckoutActorState["buyer"];

  @bookingCheckoutActorState.field("requiredProfile")
  private requiredProfile!: BookingCheckoutActorState["requiredProfile"];

  @bookingCheckoutActorState.field("forSomeoneElse")
  private forSomeoneElse!: BookingCheckoutActorState["forSomeoneElse"];

  @bookingCheckoutActorState.field("recipientType")
  private recipientType!: BookingCheckoutActorState["recipientType"];

  /** The "who are you booking for" modal (family vs guest picker). */
  @bookingCheckoutActorState.field("recipientModalOpen")
  private recipientModalOpen!: BookingCheckoutActorState["recipientModalOpen"];

  @bookingCheckoutActorState.field("includeSelf")
  private includeSelf!: BookingCheckoutActorState["includeSelf"];

  @bookingCheckoutActorState.field("people")
  private people!: BookingCheckoutActorState["people"];

  @bookingCheckoutActorState.field("activeSpot")
  private activeSpot!: BookingCheckoutActorState["activeSpot"];

  @bookingCheckoutActorState.field("discount")
  private discount!: BookingCheckoutActorState["discount"];

  /** Semantic workflow state is owned by the typed submission statechart. */
  private get placed(): boolean {
    return this.submission.succeeded;
  }

  private get submitting(): boolean {
    return this.submission.busy;
  }

  private get chargeCompleted(): boolean {
    return this.submission.paymentCommitted || this.submission.succeeded;
  }
  @bookingCheckoutActorState.field("placedOrderId")
  private placedOrderId!: BookingCheckoutActorState["placedOrderId"];

  @bookingCheckoutActorState.field("signedInName")
  private signedInName!: BookingCheckoutActorState["signedInName"];

  /** Signed-in member context (saved cards + credits). Null = guest. */
  @bookingCheckoutActorState.field("memberCtx")
  private memberCtx!: BookingCheckoutActorState["memberCtx"];

  /** Member's activity-scoped credit-packs (customer-app source). */
  @bookingCheckoutActorState.field("regCredits")
  private regCredits!: BookingCheckoutActorState["regCredits"];

  /** Canonical owned $0 membership tiers for the signed-in attendee. */
  @bookingCheckoutActorState.field("ownedAccessProductIds")
  private ownedAccessProductIds!: BookingCheckoutActorState["ownedAccessProductIds"];
  /** PLAT-6791: every usable owned membership for this activity. */
  @bookingCheckoutActorState.field("ownedProductIds")
  private ownedProductIds!: BookingCheckoutActorState["ownedProductIds"];
  /** PLAT-6791: drop-in price after the member's owned-membership discount. */
  @bookingCheckoutActorState.field("memberDropInPrice")
  private memberDropInPrice!: BookingCheckoutActorState["memberDropInPrice"];
  @bookingCheckoutActorState.field("deferredMemberships")
  private deferredMemberships!: BookingCheckoutActorState["deferredMemberships"];
  /** PLAT-6805: family attendees' own memberships (see `loadFamilyAccess`). */
  @bookingCheckoutActorState.field("familyAccess")
  private familyAccess!: BookingCheckoutActorState["familyAccess"];
  /** Family user ids whose access is loading or loaded for this activity. */
  private familyAccessRequested = new Set<number>();

  /** Member's family members for the "booking for someone else" list. */
  @bookingCheckoutActorState.field("familyMembers")
  private familyMembers!: BookingCheckoutActorState["familyMembers"];

  // Waiver, referral, promo, and purchase terms.
  @bookingCheckoutActorState.field("waiverOpen")
  private waiverOpen!: BookingCheckoutActorState["waiverOpen"];

  @bookingCheckoutActorState.field("waiverChecked")
  private waiverChecked!: BookingCheckoutActorState["waiverChecked"];

  /** Signed-in member already has this activity's waiver on file. */
  @bookingCheckoutActorState.field("waiverOnFile")
  private waiverOnFile!: BookingCheckoutActorState["waiverOnFile"];

  /**
   * CK4: per-family-member waiver opt-in, keyed by the person's index in
   * `people`. When `on`, the buyer signs this activity's waiver on behalf of
   * that family member. Purely additive UI — it surfaces the same waiver copy
   * the buyer sees and does not gate the step.
   */
  @bookingCheckoutActorState.field("familyWaiver")
  private familyWaiver!: BookingCheckoutActorState["familyWaiver"];

  /** The class's real waiver form (rendered + submitted unless already on file). */
  @bookingCheckoutActorState.field("waiverForm")
  private waiverForm!: BookingCheckoutActorState["waiverForm"];

  @bookingCheckoutActorState.field("termsForm")
  private termsForm!: BookingCheckoutActorState["termsForm"];

  /** Org intake forms (non-waiver) for guests to fill; suppressed for members. */
  @bookingCheckoutActorState.field("intakeForms")
  private intakeForms!: BookingCheckoutActorState["intakeForms"];

  /**
   * Prior-trigger forms belonging to the PRODUCTS being charged, as opposed to
   * `intakeForms`, which belong to the class/appointment.
   *
   * These are two independent configuration surfaces and they routinely hold
   * different forms — the org that surfaced this had "Data Collection Form" on
   * the activity and "Waiver" on every product. Only the product's form is
   * enforced by the charge, so collecting the activity's alone produced a
   * checkout that could be completed in full and still be rejected.
   */
  @bookingCheckoutActorState.field("productForms")
  private productForms!: BookingCheckoutActorState["productForms"];

  /** Form-id set `loadProductForms()` last resolved, so it reloads only on change. */
  private loadedProductFormKey = "";
  private productFormsSeq = 0;
  private productFormsPending = false;

  @bookingCheckoutActorState.field("intakeAnswers")
  private intakeAnswers!: BookingCheckoutActorState["intakeAnswers"];

  @bookingCheckoutActorState.field("intakeError")
  private intakeError!: BookingCheckoutActorState["intakeError"];

  @bookingCheckoutActorState.field("bookingError")
  private bookingError!: BookingCheckoutActorState["bookingError"];

  /** Submitted intake-form responses, attached to registration after charge. */
  private intakeSubmissions: { formId: number; formResponseId: string }[] = [];

  /**
   * Submitted PRODUCT prior-form responses, kept apart from `intakeSubmissions`.
   *
   * These belong to the charge, not the registration. Register only validates
   * the activity's own forms and ignores anything else in `formSubmissions` —
   * except that, with no activity waiver to match, it falls back to
   * `formSubmissions[0]` to derive `registeringUserId` and rejects the booking
   * if that user isn't the registrant. Sending a product form there would put
   * an unrelated response in that slot.
   */
  private productSubmissions: { formId: number; formResponseId: string }[] = [];
  /** Reuse unchanged answers between quote preparation and final booking. */
  private savedFormResponses = new Map<string, Promise<string | null>>();

  /**
   * The buyer's guest roster, created before any form is submitted so the
   * waiver/intake responses can be filed against a real person.
   *
   * Held for the life of the checkout because `create-guest` is not idempotent:
   * asking twice for the same email 401s with `guest_checkout_account_exists`.
   * The payment step and the post-charge guest registration both reuse this
   * handle rather than minting their own.
   */
  private guestRoster: GuestRosterHandle | null = null;
  /** True while the CTA's own charge runs; see `placeBooking()`. */
  private bookingPrepared = false;
  private deferredBookingAccess: DeferredBookingAccessDto | null = null;
  private deferredBookingFinished = false;
  private existingDeferredConsent = false;
  private preparedDeferredKey = '';
  private get pendingDeferredKey(): string {
    const selected = this.deferredSelection;
    return [selected?.tier?.deferredMembershipPurchaseId, selected?.att.a.familyRosterId || this.memberCtx?.rosterId || this.guestRoster?.rosterId,
      this.orgId, this.eventSlug, this.activityId, this.appointmentTypeId, this.appointmentProviderUserId, this.startIso].join(':');
  }

  private get deferredSelection() {
    const isDeferred = (tier?: PricingTier) => !!(tier?.deferredChargeSettings?.enabled || tier?.deferredMembershipPurchaseId);
    return this.attendees().map((att) => {
      const selected = this.tiers.find((tier) => tier.id === att.a.tierId);
      // Selecting an owned credit leaves the purchase tier in place. Only the
      // product actually covering this attendee may supply reservation terms.
      const coveredProductId = att.a.memberCreditProductId || att.a.memberAccessProductId;
      const selectedCoversAttendee = selected && coveredProductId &&
        (selected.id === coveredProductId || familyAccessProductId(
          selected.id, att.a.familyUserId, [coveredProductId],
        ) === coveredProductId);
      const covered = selectedCoversAttendee ? selected : this.tiers.find((tier) => tier.id === coveredProductId);
      return { att, tier: coveredProductId ? covered : selected };
    }).find(({ tier }) => isDeferred(tier));
  }

  private async prepareMembershipBooking(purchaseId: string): Promise<void> {
    const selection = this.deferredSelection;
    if (!selection?.tier || this.attendees().length !== 1) throw new Error('Book one attendee at a time with a deferred membership.');
    const rosterId = selection.att.a.familyRosterId || this.memberCtx?.rosterId || this.guestRoster?.rosterId || this.paymentEl?.guestRoster?.rosterId;
    const guest = this.guestRoster ?? this.paymentEl?.guestRoster;
    if (!rosterId) throw new Error('Complete your details before reserving this membership.');
    this.guestRoster = guest ?? null;
    this.deferredBookingAccess = await prepareDeferredBooking(this.orgId, {
      rosterId, purchaseId, ...(!this.memberCtx && guest?.checkoutToken ? { checkoutToken: guest.checkoutToken } : {}),
      ...(this.isAppt ? { appointmentTypeId: this.appointmentTypeId, providerUserId: this.appointmentProviderUserId, startsAt: this.startIso }
        : { eventSlug: this.eventSlug, activityId: this.activityId }),
    }, this.runtime.api);
    this.preparedDeferredKey = this.pendingDeferredKey;
  }

  private async finishMembershipBooking(purchase?: DeferredMembershipPurchaseDto): Promise<void> {
    const id = purchase?.id ?? this.deferredSelection?.tier?.deferredMembershipPurchaseId;
    if (!id) throw new Error('Membership reservation could not be found.');
    await this.prepareMembershipBooking(id);
    const access = this.deferredBookingAccess!;
    if (!access.completedBookingId) {
      if (this.isAppt) {
        if (!this.finalizeBooking) throw new Error('Appointment booking is unavailable. Your membership remains reserved.');
        await this.finalizeBooking({ stripeProductId: this.chargeStripeProductId(),
          deferredMembershipPurchaseId: access.purchaseId, deferredBeneficiaryRosterId: access.beneficiaryRosterId,
          checkoutToken: this.memberCtx ? undefined : this.guestRoster?.checkoutToken,
          consumeCreditForProductId: access.allAccess ? undefined : access.productId,
          accessedWithProductId: access.allAccess ? access.productId : undefined,
          targetUserId: access.beneficiaryUserId, currentUserId: this.memberCtx?.userId,
          intakeSubmissions: this.intakeSubmissions, buyer: { ...this.buyer },
        });
      } else {
        const att = this.deferredSelection!.att;
        await registerGuest(this.orgId, this.orgSlug, this.eventSlug, {
          activityId: this.activityId, purchasedForRosterId: access.beneficiaryRosterId,
          deferredMembershipPurchaseId: access.purchaseId, deferredBeneficiaryRosterId: access.beneficiaryRosterId,
          checkoutToken: this.memberCtx ? undefined : this.guestRoster?.checkoutToken,
          consumeCreditForProductId: access.allAccess ? undefined : access.productId,
          accessedWithProductId: access.allAccess ? access.productId : undefined,
          spotHoldId: att.a.holdId || undefined,
          formSubmissions: att.isBuyer ? this.intakeSubmissions : await this.formsForAttendee(att), omitBookerWaiver: this.waiverOnFile,
        }, ...runtimeApiArguments(this.runtime));
      }
    }
    this.placedOrderId = access.purchaseId;
    this.deferredBookingFinished = true;
  }

  /** Rosters minted for additional (non-buyer) guest attendees, keyed by email. */
  private attendeeRosters = new Map<string, GuestRosterHandle>();

  @bookingCheckoutActorState.field("promoCode")
  private promoCode!: BookingCheckoutActorState["promoCode"];

  @bookingCheckoutActorState.field("appliedCouponId")
  private appliedCouponId!: BookingCheckoutActorState["appliedCouponId"];

  @bookingCheckoutActorState.field("promoError")
  private promoError!: BookingCheckoutActorState["promoError"];

  @bookingCheckoutActorState.field("promoBusy")
  private promoBusy!: BookingCheckoutActorState["promoBusy"];

  @bookingCheckoutActorState.field("termsOpen")
  private termsOpen!: BookingCheckoutActorState["termsOpen"];

  @bookingCheckoutActorState.field("termsChecked")
  private termsChecked!: BookingCheckoutActorState["termsChecked"];

  @bookingCheckoutActorState.field("termsConsents")
  private termsConsents!: BookingCheckoutActorState["termsConsents"];

  @bookingCheckoutActorState.field("buyOptionsOpen")
  private buyOptionsOpen!: BookingCheckoutActorState["buyOptionsOpen"];

  @bookingCheckoutActorState.field("step1Touched")
  private step1Touched!: BookingCheckoutActorState["step1Touched"];

  // ── Derived ──

  private get buyerAttends(): boolean {
    return !this.forSomeoneElse || this.includeSelf;
  }

  /**
   * Whether this class's grouping actually requires a waiver. Driven by the
   * form requirements in the schedule payload — when absent, the waiver section
   * is not shown at all (some groupings have no waiver).
   */
  private get hasWaiverRequirement(): boolean {
    return this.classForms.some((f) => f.type === "waiver");
  }

  /** At least one attendee is booking with an owned credit-pack (not paying). */
  private get creditCovered(): boolean {
    return checkoutMath.creditCoveredAttendees(this.attendees()).length > 0;
  }

  /**
   * PLAT-5624: server-resolved appointment pricing (see
   * {@link applyAppointmentPayOptions}). Nothing here is trusted until
   * `apptPayResolved` is true.
   */
  @bookingCheckoutActorState.field("apptPayResolved")
  private apptPayResolved!: BookingCheckoutActorState["apptPayResolved"];

  /** The org's own "this appointment type is free" verdict, from the API. */
  @bookingCheckoutActorState.field("apptTypeIsFree")
  private apptTypeIsFree!: BookingCheckoutActorState["apptTypeIsFree"];

  /** Real Stripe product id for the drop-in charge, resolved server-side. */
  @bookingCheckoutActorState.field("resolvedServiceProductId")
  private resolvedServiceProductId!: BookingCheckoutActorState["resolvedServiceProductId"];

  /** Authoritative drop-in price in dollars, resolved server-side. */
  @bookingCheckoutActorState.field("resolvedServicePrice")
  private resolvedServicePrice!: BookingCheckoutActorState["resolvedServicePrice"];

  /** Why pricing could not be resolved — shown instead of a bogus "Free" CTA. */
  @bookingCheckoutActorState.field("apptPayError")
  private apptPayError!: BookingCheckoutActorState["apptPayError"];

  /**
   * PLAT-5624: the price this appointment actually charges.
   *
   * Prefers the server-resolved drop-in price over the public schedule feed's
   * `price`, which is 0 for a credit-priced service and therefore cannot be
   * read as "costs nothing".
   */
  private get effectiveServicePrice(): number {
    return this.resolvedServicePrice > 0
      ? this.resolvedServicePrice
      : this.price;
  }

  /**
   * PLAT-5624 fail-closed money guard.
   *
   * A $0 appointment booking is only legitimate when something SAYS it is
   * free — the org's `isFree` switch, the resolved pay options, or a credit
   * that actually covers it. It must never be the residue of a pricing lookup
   * that failed or hasn't finished, which is how a credit-priced "Couples
   * Massage" became bookable for nothing.
   *
   * The class flow is untouched: it resolves its tiers from the activity feed
   * and has its own free/credit semantics.
   */
  private get freeBookingAllowed(): boolean {
    if (!this.isAppt) return true;
    // A credit the buyer holds is a real, accounted-for payment.
    if (this.creditCovered) return true;
    // PLAT-6791/6805: so is an attendee's own All Access membership.
    if (
      this.attendees().length > 0 &&
      this.attendees().every(({ a }) => !!a.memberAccessProductId)
    ) {
      return true;
    }
    // The org explicitly configured the service as free.
    if (this.serviceIsFree) return true;
    if (this.attendees().length > 0 && this.attendees().every(({ a, isBuyer }) =>
      isBuyer
        ? this.apptPayResolved && this.memberDropInPrice === 0
        : !!a.familyUserId && this.familyAccess[a.familyUserId]?.memberDropInPrice === 0,
    )) return true;
    // …or the resolved pay options say so.
    if (this.apptPayResolved && this.apptTypeIsFree) return true;
    // Priced service with a resolved, positive price → not a free booking at
    // all; the paid path handles it. Anything else is unresolved: refuse.
    return false;
  }

  /**
   * PLAT-5624: pricing is still unknown (or failed), so neither "pay" nor
   * "book for free" is a safe action. Drives the CTA's disabled state and the
   * explanatory copy.
   */
  private get apptPricingUnresolved(): boolean {
    if (!this.isAppt) return false;
    if (this.total > 0) return false; // a real charge is on the table
    return !this.freeBookingAllowed;
  }

  private get extras(): Attendee[] {
    return this.forSomeoneElse && this.recipientType ? this.people : [];
  }

  /** Ordered attendees with display labels; buyer first when attending. */
  private attendees(): { a: Attendee; label: string; isBuyer: boolean }[] {
    const list: { a: Attendee; label: string; isBuyer: boolean }[] = [];
    if (this.buyerAttends) {
      const name = `${this.buyer.firstName} ${this.buyer.lastName}`.trim();
      list.push({ a: this.buyer, label: name || "You", isBuyer: true });
    }
    const noun = this.recipientType === "guest" ? "Guest" : "Family member";
    this.extras.forEach((p, i) => {
      const name = `${p.firstName} ${p.lastName}`.trim();
      list.push({ a: p, label: name || `${noun} ${i + 1}`, isBuyer: false });
    });
    return list;
  }

  private get familyPackAvailable(): boolean {
    return checkoutMath.familyPackAvailable(
      this.recipientType,
      this.attendees(),
      this.tiers,
    );
  }

  /**
   * Credit-packs the signed-in member already holds that apply to THIS class —
   * i.e. a held credit whose product matches one of the class's pack/membership
   * tiers (tier.id is the Zipper product id) and still has uses remaining
   * (null = unlimited membership). Empty for guests and when nothing matches, so
   * the "use your credit" option only surfaces when it's genuinely redeemable.
   */
  private memberCreditsFor(relationship: "self" | "family" | "guest"): {
    productId: string;
    punchCardId?: string;
    guestsOnly?: boolean;
    label: string;
    remaining: number | null;
  }[] {
    // Source from the member's activity-scoped registration-context credits
    // (customer-app parity): only usable ones, in the same priority order.
    // SET-19 / CHK-20: the member's OWN credit may only be applied to a
    // family/guest attendee when the product permits that relationship's
    // sharing — otherwise it is withheld as a payment option.
    return sortMemberCredits(
      this.regCredits.filter(
        (c) =>
          isMemberCreditUsable(c, relationship) && isMemberCreditShareableTo(c, relationship),
      ),
    ).map((c) => ({
      productId: c.productId,
      punchCardId: this.isAppt ? undefined : c.punchCardId,
      guestsOnly: c.guestsOnly,
      label: c.productName || "Class pack",
      remaining: c.isUnlimited ? null : c.numberOfUsesRemaining,
    }));
  }

  private lineItems(): checkoutMath.LineItem[] {
    if (this.isAppt) {
      if (this.tiers.length) {
        return checkoutMath.lineItems(
          this.attendees(),
          this.tiers,
          this.familyPackAvailable,
        );
      }
      // PLAT-5624: the server-resolved price, not the schedule feed's `price`
      // — see effectiveServicePrice. A credit-priced service reports 0 there,
      // which is what rendered "Couples Massage — Free / Total $0.00".
      const amount = this.effectiveServicePrice;
      if (amount <= 0) {
        return [
          {
            label: this.serviceName || "Appointment",
            sub: this.providerName || "First available",
            amount: 0,
            free: true,
            credit: !!this.buyer.memberCreditProductId,
          },
        ];
      }
      return [
        {
          label: this.serviceName || "Appointment",
          sub: this.providerName || "First available",
          amount,
          free: false,
          credit: false,
        },
      ];
    }
    if (!this.tiers.length) {
      if (this.price <= 0) {
        return this.attendees().map(({ a, label }) => ({
          label,
          sub: a.memberCreditProductId ? "Your credit pack" : "Drop-in",
          amount: 0,
          free: true,
          credit: !!a.memberCreditProductId,
        }));
      }
      return this.attendees().map(({ label }) => ({
        label,
        sub: "Drop-in",
        amount: this.price,
        free: false,
        credit: false,
      }));
    }
    return checkoutMath.lineItems(
      this.attendees(),
      this.tiers,
      this.familyPackAvailable,
    );
  }

  private get subtotal(): number {
    return checkoutMath.sumLineItems(this.lineItems());
  }

  private get total(): number {
    return checkoutMath.applyDiscount(this.subtotal, this.discount);
  }

  /**
   * What the CTA promises the buyer. Once <zipper-payment> has quoted the cart
   * this is the charge itself — tax and any passed-on platform fee included —
   * rather than the pre-tax cart total the button used to name. Falls back to
   * the cart's arithmetic, which the summary qualifies with "Taxes and fees
   * calculated at checkout".
   */
  private get amountDue(): string {
    return this.quote
      ? formatCents(this.quote.totalInCents, this.currency)
      : formatShortAmount(this.total, this.currency);
  }

  // ── Step validation ──

  /**
   * The embed's own `requirePhone` option ORed with the org's
   * `requiredSignupFields.phoneNumber`. Only the latter is enforced by the API,
   * so an embed that never set `requirePhone` still has to collect a phone when
   * the org demands one.
   */
  private get phoneRequired(): boolean {
    return isPhoneRequired(this.requirePhone, this.requiredSignupFields);
  }

  /**
   * Whether this checkout will provision its own roster through `create-guest`.
   * A signed-in member already has one, so the org's required-signup-fields
   * policy has nothing to attach to and is not re-asked of them.
   */
  private get isGuestCheckout(): boolean {
    return !this.memberCtx;
  }

  /**
   * Org-required DOB / emergency contact. Only gates the GUEST path: a
   * signed-in member already has a roster, so `create-guest` never runs and the
   * policy does not apply to them.
   */
  private get requiredProfileSatisfied(): boolean {
    if (!this.isGuestCheckout) return true;
    return isRequiredProfileComplete(
      this.requiredSignupFields,
      this.requiredProfile,
    );
  }

  private stepValid(step: number): boolean {
    if (step === 1) {
      const okBuyer =
        !!(this.buyer.firstName && this.buyer.lastName) &&
        checkoutMath.isValidEmail(this.buyer.email) &&
        (!this.phoneRequired || checkoutMath.hasValidPhone(this.buyer.phone)) &&
        !checkoutMath.hasInvalidOptionalPhone(this.buyer.phone) &&
        this.requiredProfileSatisfied;
      const okExtras =
        !this.forSomeoneElse ||
        (!!this.recipientType &&
          this.extras.every((p) => p.firstName && p.lastName));
      // Each extra attendee's own copy of the activity's forms. Gated HERE
      // rather than discovered post-charge, which is where the API used to
      // reject the half-answered response this checkout filed for them.
      const okExtraForms = this.extras.every(
        (p, i) => !this.attendeeFormErrors(p, i).length,
      );
      const waiverForm = this.waiverForm;
      const okWaiver =
        !this.buyerAttends || !this.hasWaiverRequirement
          ? true // no waiver on this grouping, or the buyer isn't attending
          : this.waiverOnFile
            ? true // already signed (require-once) — don't block
            : waiverForm
              ? // Connie 7/14+7/20: the accordion checkbox is the explicit
                // "I agree". It is NOT a substitute for the form — every
                // required question on the real waiver has to be answered too,
                // by the same rules the dashboard applies (see questionError).
                this.waiverChecked &&
                waiverForm.questions.every(
                  (q) => !this.questionError(waiverForm.id, q),
                )
              : // C1: the class declares a waiver but loadForms() resolved no
                // usable form (it was filtered out for having neither body text
                // nor questions). renderWaiver() renders nothing in that state,
                // so gating here would dead-end checkout with "Complete the
                // liability waiver" and no waiver to complete. Once forms have
                // resolved, an empty waiver is nothing to consent to — allow.
                this.formsResolved;
      // Guests must answer the class's intake questions; everyone must answer
      // the charged product's prior form. See `answerableForms`.
      const okIntake = this.answerableForms().every((f) =>
        f.questions.every((q) => !this.questionError(f.id, q)),
      );
      return (
        okBuyer &&
        okExtras &&
        okExtraForms &&
        okWaiver &&
        okIntake &&
        (!this.isAppt || this.attendees().length <= this.totalCapacity)
      );
    }
    if (step === 2) return this.attendees().every(({ a }) => !!a.seat);
    if (step === 3) return !this.catalogPending && !this.payChoicePending;
    return true;
  }

  /**
   * PLAT-6800: some paying attendee has no real payment option yet.
   *
   * Attendees start on `tierId: "dropin"`. A class with no drop-in has no such
   * tier, and `findTier` then falls back to `tiers[0]` (often a membership), so
   * the summary quoted that fallback and Payment would have charged it without
   * the visitor ever choosing it. Credit- and family-pack-covered attendees
   * aren't charged, so they never block.
   */
  private get payChoicePending(): boolean {
    if (!this.tiers.length)
      return this.catalogRemovedAllChoices && this.chargedAttendees().length > 0;
    return this.chargedAttendees().some(
      ({ a }) => !this.tiers.some((t) => t.id === a.tierId),
    );
  }

  private paymentChoiceUnavailable(a: Attendee, isBuyer: boolean): boolean {
    if (
      this.catalogPending || !this.payChoicePending ||
      a.memberCreditProductId || a.memberAccessProductId
    ) return false;
    const relationship = isBuyer
      ? "self"
      : this.recipientType === "guest" ? "guest" : "family";
    const canShare = this.familyPackAvailable &&
      !isPack(checkoutMath.findTier(this.tiers, a.tierId));
    return !this.tiersForAttendee(isBuyer, a.familyUserId).length &&
      !this.memberCreditsFor(relationship).length && !canShare;
  }

  private get paymentOptionsUnavailable(): boolean {
    return this.attendees().some(({ a, isBuyer }) =>
      this.paymentChoiceUnavailable(a, isBuyer),
    );
  }

  /** PLAT-6800: open "Choose how to pay" from anywhere and bring it into view. */
  private openPayChoice(): void {
    this.setActiveStep(3);
    void this.updateComplete.then(() => {
      const target = this.renderRoot.querySelector('[data-step="3"]');
      if (target) scrollToStart(target);
    });
  }

  private step1Errors(): string[] {
    const errs: string[] = [];
    // 2b: a forms load failure is reported first and unconditionally.
    // loadForms() only runs when the grouping actually declares forms, so a
    // rejection always means the checkout is missing real requirements — the
    // waiver (which hard-blocks Continue) and/or the intake questions. It is
    // the one step-1 error the visitor cannot fix by typing, hence the retry.
    if (this.formsError) {
      errs.push(
        "Couldn't load the liability waiver. Check your connection and try again.",
      );
    }
    if (!this.buyer.firstName || !this.buyer.lastName) {
      errs.push("Enter your first and last name.");
    }
    if (!checkoutMath.isValidEmail(this.buyer.email)) {
      errs.push("Enter a valid email address.");
    }
    if (this.phoneRequired && !checkoutMath.hasValidPhone(this.buyer.phone)) {
      errs.push("Enter a valid phone number; include + and a country code if needed.");
    } else if (checkoutMath.hasInvalidOptionalPhone(this.buyer.phone)) {
      errs.push("Enter a valid phone number (include + and a country code if needed) or leave the field blank.");
    }
    // Name each missing org-required field. A single "complete your profile"
    // is unactionable when the studio requires three separate things.
    if (this.isGuestCheckout) {
      for (const missing of missingRequiredProfileFields(
        this.requiredSignupFields,
        this.requiredProfile,
      )) {
        errs.push(missing === 'dateOfBirth'
          ? dateOfBirthError(this.requiredSignupFields, this.requiredProfile.dateOfBirth) || REQUIRED_PROFILE_ERRORS.dateOfBirth
          : REQUIRED_PROFILE_ERRORS[missing]);
      }
    }
    if (this.buyerAttends && this.hasWaiverRequirement && !this.waiverOnFile) {
      // C1: no usable waiver form resolved ⇒ no waiver block is rendered, so
      // never surface an error the visitor cannot act on.
      const waiverForm = this.waiverForm;
      if (waiverForm) {
        if (!this.waiverChecked) {
          errs.push("Agree to the liability waiver before continuing.");
        }
        // Name the questions that are actually unanswered. One opaque
        // "complete the liability waiver" is unactionable on a real waiver
        // form — the health waiver on staging has thirteen questions.
        for (const q of waiverForm.questions) {
          const qErr = this.questionError(waiverForm.id, q);
          if (qErr) errs.push(`${q.label}: ${qErr}`);
        }
      }
    }
    // Same treatment as the waiver — name the offending question rather
    // than emitting one generic "complete required fields".
    for (const f of this.answerableForms()) {
      for (const q of f.questions) {
        const qErr = this.questionError(f.id, q);
        if (qErr) errs.push(`${q.label}: ${qErr}`);
      }
    }
    if (
      this.forSomeoneElse &&
      this.recipientType &&
      this.extras.some((p) => !p.firstName || !p.lastName)
    ) {
      errs.push("Enter a first and last name for each guest or family member.");
    }
    // Prefixed with whose answer is missing: the same question appears once per
    // attendee, so a bare "DOB: A date must be selected." three times over
    // names nobody. Their own name once they have one, their card's heading
    // until then — the same label the card itself carries.
    const noun = this.recipientType === "guest" ? "Guest" : "Family member";
    this.extras.forEach((p, i) => {
      const who = `${p.firstName} ${p.lastName}`.trim() || `${noun} ${i + 1}`;
      for (const err of this.attendeeFormErrors(p, i)) {
        errs.push(`${who} — ${err}`);
      }
    });
    if (this.isAppt && this.attendees().length > this.totalCapacity) {
      errs.push(
        `This appointment allows up to ${this.totalCapacity} participant${this.totalCapacity === 1 ? "" : "s"}.`,
      );
    }
    return errs;
  }

  private get maxExtraSlots(): number {
    if (!this.isAppt) return MAX_EXTRA;
    const cap = this.appointmentCapacity > 0 ? this.appointmentCapacity : 1;
    return Math.max(0, cap - (this.buyerAttends ? 1 : 0));
  }

  private get totalCapacity(): number {
    if (!this.isAppt) return MAX_EXTRA;
    return this.appointmentCapacity > 0 ? this.appointmentCapacity : 1;
  }

  // ── Mutation helpers (replace objects so Lit re-renders) ──

  private patchBuyer(k: keyof Attendee, v: string | boolean): void {
    const val =
      k === "phone" && typeof v === "string"
        ? checkoutMath.formatPhoneInput(v)
        : v;
    this.buyer = { ...this.buyer, [k]: val };
  }

  private patchPerson(i: number, k: keyof Attendee, v: string | boolean): void {
    const val =
      k === "phone" && typeof v === "string"
        ? checkoutMath.formatPhoneInput(v)
        : v;
    this.people = this.people.map((p, idx) =>
      idx === i ? { ...p, [k]: val } : p,
    );
  }

  private setCount(n: number): void {
    const max = this.isAppt ? Math.max(1, this.maxExtraSlots) : MAX_EXTRA;
    const next = Math.max(1, Math.min(max, n));
    const futureCount = (this.buyerAttends ? 1 : 0) + next;
    if (this.isAppt && futureCount > this.totalCapacity) {
      return;
    }
    const rows = this.people.slice(0, next);
    while (rows.length < next) rows.push(emptyAttendee());
    this.people = rows;
    // A row that was counted away takes its answers with it — otherwise
    // counting back up hands a brand-new blank attendee the last one's.
    this.remapAttendeeAnswers((idx) => (idx < next ? idx : null));
  }

  private toggleSomeoneElse(): void {
    this.forSomeoneElse = !this.forSomeoneElse;
    if (this.forSomeoneElse) {
      if (this.isAppt && this.totalCapacity === 1) this.includeSelf = false;
      // Prompt for who they're booking for (family vs guest).
      this.recipientModalOpen = true;
    } else {
      this.recipientType = "";
    }
  }

  private chooseRecipient(type: "family" | "guest"): void {
    if (this.recipientType !== type) {
      this.recipientType = type;
      this.people = [emptyAttendee()];
    }
    // SC9: both guest AND family confirm the choice by closing the lightbox
    // immediately — the member panel is already rendered behind it (a blank row
    // to fill in). Users can re-open via "Change" to pick saved family members
    // from the list. Previously only guest closed while family stayed open,
    // which left the lightbox hanging over the added panel.
    this.recipientModalOpen = false;
  }

  private addFamilyMember(m: EmbedFamilyMemberWithRoster): void {
    if (!this.people.some((p) => p.familyUserId === m.userId)) {
      const person: Attendee = {
        ...emptyAttendee(),
        firstName: m.firstName,
        lastName: m.lastName,
        familyUserId: m.userId,
        familyRosterId: m.rosterId,
      };
      const blank = this.people.findIndex(
        (p) => !p.firstName && !p.lastName && !p.familyUserId,
      );
      this.people =
        blank >= 0
          ? this.people.map((p, i) => (i === blank ? person : p))
          : [...this.people, person].slice(
              0,
              this.isAppt ? this.maxExtraSlots : MAX_EXTRA,
            );
    }
    // Picking a specific person confirms the choice — dismiss the modal (the
    // form row is already added/updated). Extra people can still be added via
    // the count stepper or by re-opening with "Change".
    this.recipientModalOpen = false;
    // PLAT-6805: their own memberships price their seat.
    if (this.familyAccess[m.userId]) this.applyFamilyAccess();
    else void this.loadFamilyAccess(m.userId);
  }

  private renderRecipientModal(): TemplateResult | typeof nothing {
    if (!this.recipientModalOpen) return nothing;
    return html`
      <div
        class="rc-overlay"
        @click=${(e: Event) => {
          if (e.target === e.currentTarget) this.recipientModalOpen = false;
        }}
      >
        <div class="rc-sheet" role="dialog" aria-modal="true">
          <div class="rc-head">
            <span>Who are you booking for?</span>
            <button
              class="rc-close"
              aria-label="Close"
              @click=${() => (this.recipientModalOpen = false)}
            >
              ✕
            </button>
          </div>
          <div
            class="rc-choices"
            role="radiogroup"
            aria-label="Who are you booking for?"
          >
            ${RECIPIENT_CHOICES.map(
              (c) =>
                html`<label
                  class="rc-choice ${
                    this.recipientType === c.value ? "sel" : ""
                  }"
                >
                  <input
                    class="rc-radio"
                    type="radio"
                    name="rc-recipient"
                    .value=${c.value}
                    .checked=${live(this.recipientType === c.value)}
                    @change=${() => this.chooseRecipient(c.value)}
                    @click=${() => this.chooseRecipient(c.value)}
                  />
                  <span class="rc-choice-t">${c.title}</span>
                  <span class="rc-choice-s">${c.sub}</span>
                </label>`,
            )}
          </div>
          ${
            this.recipientType === "family"
              ? html`<div class="rc-list">
                    ${
                    this.familyMembers.length
                      ? this.familyMembers.map((m) => {
                          const picked = this.people.some(
                            (p) => p.familyUserId === m.userId,
                          );
                          return html`<button
                            class="rc-opt ${picked ? "sel" : ""}"
                            @click=${() => this.addFamilyMember(m)}
                          >
                            <span>${m.firstName} ${m.lastName}</span>
                            ${picked ? ICON_CHECK : nothing}
                          </button>`;
                        })
                      : html`<div class="rc-empty">
                          No family members on your account yet.
                        </div>`
                  }
                  </div>
                  <button
                    class="zui-cta"
                    @click=${() => (this.recipientModalOpen = false)}
                  >
                    Done
                  </button>`
              : nothing
          }
        </div>
      </div>
    `;
  }

  private assignSeat(seat: string): void {
    if (!this.roomAvail && TAKEN_SEATS.has(seat)) return; // generic grid only
    const list = this.attendees();
    if (list.some(({ a }) => a.seat === seat)) return; // already taken by us
    const target = list[this.activeSpot];
    if (!target) return;
    if (target.isBuyer) this.patchBuyer("seat", seat);
    else {
      const idx = this.extras.indexOf(target.a);
      this.patchPerson(idx, "seat", seat);
    }
    // advance to next unassigned
    const next = list.findIndex(({ a }, i) => i > this.activeSpot && !a.seat);
    this.activeSpot = next === -1 ? this.activeSpot : next;
  }

  private async applyPromo(): Promise<void> {
    if (this.chargeCompleted) return;
    if (this.deferredSelection) { this.promoError = 'Discount codes are unavailable for deferred memberships.'; return; }
    const code = this.promoCode.trim();
    if (!code || this.promoBusy) return;
    this.promoBusy = true;
    this.promoError = "";
    try {
      const r = await validatePromotionCode(this.orgId, {
        code,
        stripeProductId:
          this.chargeItems()[0]?.stripeProductId ||
          this.serviceStripeProductId ||
          undefined,
        customerEmail: this.buyer.email || undefined,
        orderAmountInCents: Math.round(this.subtotal * 100),
      });
      if (!r.valid) {
        this.discount = 0;
        this.appliedCouponId = "";
        this.promoError = r.errorMessage || "That code isn’t valid.";
        return;
      }
      this.appliedCouponId = r.couponId ?? "";
      this.discount = promoDiscount(r, this.subtotal);
    } catch {
      this.discount = 0;
      this.appliedCouponId = "";
      this.promoError = "Could not check that code. Try again.";
    } finally {
      this.promoBusy = false;
    }
  }

  private goNext(step: number): void {
    if (step === 1) this.step1Touched = true;
    if (!this.stepValid(step)) return;
    this.done = new Set(this.done).add(step);
    const idx = this.visibleSteps.indexOf(step);
    // PLAT-6800: "Choose how to pay" can be opened before details are done.
    // Finish any earlier step still incomplete before moving forward.
    const earlier = this.visibleSteps
      .slice(0, idx)
      .find((s) => !this.stepValid(s));
    if (earlier !== undefined) {
      if (earlier === 1) this.step1Touched = true;
      this.setActiveStep(earlier);
      return;
    }
    this.setActiveStep(this.visibleSteps[idx + 1] ?? step);
  }

  /**
   * Change the active step while keeping the viewport visually anchored.
   * Opening/closing a step body changes page height above the fold —
   * collapsing the step you just completed shrinks a sibling above whatever
   * you're currently looking at, which otherwise yanks the host page's
   * scroll position out from under you. Re-anchors on the target step
   * card's own position instead of trying to preserve a raw pixel offset.
   */
  private setActiveStep(step: number): void {
    if (this.chargeCompleted) return;
    const card = this.renderRoot.querySelector(
      `[data-step="${step}"]`,
    ) as HTMLElement | null;
    const before = card?.getBoundingClientRect().top;
    this.activeStep = step;
    if (
      step === 4 &&
      this.chargeItems().length > 0 &&
      this.total > 0 &&
      !this.paymentEl
    ) {
      void this.updateComplete.then(() => this.openPayment());
    }
    if (before === undefined) return;
    void this.updateComplete.then(() => {
      const target = this.renderRoot.querySelector(
        `[data-step="${step}"]`,
      ) as HTMLElement | null;
      const after = target?.getBoundingClientRect().top;
      if (after === undefined) return;
      const delta = after - before;
      if (!delta) return;
      if (target) scrollRootBy(target, delta);
    });
  }

  private async syncMemberPhone(): Promise<void> {
    if (!this.orgId || !this.memberCtx || !this.buyer.phone) return;
    if (!checkoutMath.hasValidPhone(this.buyer.phone)) return;
    try {
      await updateMemberPhone(this.orgId, this.buyer.phone);
    } catch {
      /* non-blocking */
    }
  }

  /**
   * PLAT-5624: finish the booking, and only THEN show the confirmation.
   *
   * `finalizeBooking` (set by `<zipper-appointments>`) creates the real
   * appointment. It used to run off the `zipper-booking-complete` event, i.e.
   * strictly after `placed` had flipped — so an appointment that failed to
   * create ("Could not find appointment type <uuid>") still rendered
   * "You're booked!" with a receipt line and calendar buttons. A failed
   * finalize now keeps the checkout on the payment step with a real error.
   */
  private async placeOrder(): Promise<void> {
    const covered = checkoutMath.creditCoveredAttendees(this.attendees());
    // PLAT-6791/6805: the attendee booking on their own All Access membership.
    const accessAtt = this.attendees().find(
      ({ a }) => !!a.memberAccessProductId && !a.memberCreditProductId,
    );
    const creditAtt = covered[0]?.att;
    const targetUserId =
      creditAtt && this.memberCtx
        ? creditAtt.a.familyUserId || this.memberCtx.userId
        : undefined;
    if (this.finalizeBooking && !this.deferredBookingFinished) {
      try {
        await this.finalizeBooking({
          stripeProductId: this.chargeStripeProductId(),
          consumeCreditForProductId: covered[0]?.productId,
          accessedWithProductId: accessAtt?.a.memberAccessProductId,
          targetUserId:
            targetUserId ??
            (accessAtt && !accessAtt.isBuyer
              ? accessAtt.a.familyUserId || undefined
              : undefined) ??
            (this.buyerAttends ? this.memberCtx?.userId : undefined) ??
            this.guestRoster?.userId,
          currentUserId: this.memberCtx?.userId,
          intakeSubmissions: this.intakeSubmissions,
          buyer: {
            firstName: this.buyer.firstName,
            lastName: this.buyer.lastName,
            email: this.buyer.email,
            phone: this.buyer.phone,
          },
        });
      } catch (e) {
        // The money (if any) has already moved, so say so plainly rather than
        // implying the booking exists. The payment-committed state enters
        // recovery so this cannot be retried into a second charge.
        const why =
          e instanceof Error ? e.message : "Please contact the studio.";
        const paid = this.total > 0;
        this.bookingError = paid
          ? `Your payment went through, but we couldn’t finish booking this appointment: ${why} Please contact the studio — do not pay again.`
          : `We couldn’t book this appointment: ${why}`;
        this.submission.fail(this.bookingError, paid);
        return;
      }
    }
    this.submission.succeed({ orderId: this.placedOrderId || undefined });
    this.dispatchEvent(
      new CustomEvent("zipper-booking-complete", {
        detail: {
          className: this.displayTitle,
          attendees: this.attendees().map(({ label, a }) => ({
            name: label,
            tier: a.tierId,
            seat: a.seat,
            useCredit: a.useCredit,
          })),
          total: this.deferredBookingFinished && this.deferredBookingAccess
            ? (this.deferredBookingAccess.triggerType === 'first_booking' ? this.deferredBookingAccess.futureChargeAmountInCents / 100 : 0)
            : this.total,
          buyer: { ...this.buyer },
          intakeSubmissions: this.intakeSubmissions,
          attendeeCount: this.attendees().length,
          stripeProductId: this.chargeStripeProductId(),
          consumeCreditForProductId: covered[0]?.productId,
          targetUserId,
          currentUserId: this.memberCtx?.userId,
        },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private close(): void {
    this.dispatchEvent(
      new CustomEvent("zipper-booking-close", {
        bubbles: true,
        composed: true,
      }),
    );
  }

  // ── Render ──

  /** Spot/seat selection is only offered when the class has spot booking on. */
  private get spotBookingEnabled(): boolean {
    return !!this.roomId;
  }

  /**
   * Visible step ids in order. Step ids are stable (1=details, 2=spots,
   * 3=pay, 4=payment) so the dispatch methods stay id-keyed; the "Pick your
   * spots" step (2) is dropped entirely for general-admission classes.
   * Appointments are a single fixed-price service for one person, so both the
   * seat step (2) and the per-attendee pay-option step (3) are dropped —
   * "Your details" → "Payment".
   */
  /**
   * PLAT-5624: guests now resolve appointment tiers too (see
   * `loadGuestAppointmentPayContext`), so "has any tier" would insert a
   * "Choose how to pay" step into every flat-priced appointment — including
   * ones whose only option is the drop-in that is already the default. Show it
   * when there is a real CHOICE (more than one option, or credits on file), or
   * when the service has no drop-in price of its own and therefore MUST be paid
   * for another way. `effectiveServicePrice` rather than `price`: the schedule
   * feed reports 0 for a credit-priced service.
   */
  private get apptShowPayStep(): boolean {
    return (
      this.tiers.length > 1 ||
      this.regCredits.length > 0 ||
      this.effectiveServicePrice <= 0
    );
  }

  private get visibleSteps(): number[] {
    if (this.isAppt) return this.apptShowPayStep ? [1, 3, 4] : [1, 4];
    return this.spotBookingEnabled ? [1, 2, 3, 4] : [1, 3, 4];
  }

  /** kind="appointment" — drives the branches that differ from the class flow. */
  private get isAppt(): boolean {
    return this.kind === "appointment";
  }

  /** Title for the header card + Order Summary (service vs class name). */
  private get displayTitle(): string {
    return this.isAppt ? this.serviceName || "Appointment" : this.className;
  }

  /** Provider/when meta parts for the header card + Order Summary. */
  private get displayMetaParts(): string[] {
    return this.isAppt
      ? [this.providerName || "First available", this.apptWhen]
      : [this.instructorName, this.classWhen];
  }

  /**
   * Up-to-two-letter initials shown in the class/instructor thumbnail when no
   * image is available (mirrors the schedule row's initials fallback so the
   * booking card and order summary never render an empty colored square).
   */
  private get thumbInitials(): string {
    const src = (
      (this.isAppt
        ? this.serviceName || this.providerName
        : this.className || this.instructorName) || ""
    ).trim();
    return src
      .split(/\s+/)
      .map((w) => w[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("");
  }

  render(): TemplateResult {
    if (this.placed) return this.renderConfirmation();
    return html`
      <div class="page">
        <button class="back" @click=${this.close}>
          ${this.isAppt ? "← Back" : "← Back to schedule"}
        </button>
        <div class="grid">
          <div class="col">
            ${this.renderClassCard()} ${this.renderSignInBanner()}
            ${this.visibleSteps.map((id, i) =>
              this.renderStep(id, STEPS[id - 1], i + 1),
            )}
          </div>
          ${this.renderSummary()}
        </div>
      </div>
    `;
  }

  private renderClassCard(): TemplateResult {
    const meta = [...this.displayMetaParts, this.duration]
      .filter(Boolean)
      .join(" · ");
    return html`
      <div class="card class-card">
        <div class="thumb">
          ${
            this.image
              ? html`<img src=${this.image} alt="" />`
              : html`<span class="thumb-initials">${this.thumbInitials}</span>`
          }
        </div>
        <div>
          <div class="ov">You're booking</div>
          <div class="cname">${this.displayTitle}</div>
          <div class="cmeta">${meta}</div>
        </div>
      </div>
    `;
  }

  /**
   * CK1: sign the member out — drop the embed session token and reset the
   * member context, then reload so the flow returns to its logged-out state
   * (member context is re-fetched on load and now resolves to a guest).
   */
  private logout(): void {
    clearEmbedToken(this.orgId);
    this.memberCtx = null;
    this.guestRoster = null;
    this.deferredBookingAccess = null; this.deferredBookingFinished = false; this.existingDeferredConsent = false;
    this.attendeeRosters.clear();
    this.signedInName = "";
    this.regCredits = [];
    this.familyMembers = [];
    this.waiverOnFile = false;
    window.location.reload();
  }

  /**
   * Open the shared customer sign-in modal and refresh the member context once
   * the customer is in. Single owner of this checkout's sign-in entry point so
   * the headline CTA can never drift from the old banner button's behaviour.
   */
  private openSignInModal(): void {
    openSignIn(this.orgId, this.accent, (name) => {
      this.signedInName = name || "there";
      void this.loadMember();
    });
  }

  /**
   * 7/25 QA: the sign-in affordance is the shared secondary button sitting on
   * the "Your details" headline row — identical to the events register flow
   * and the shop checkout — instead of an accent pill in the page-level banner
   * that read with the same weight as the primary "Confirm booking" CTA.
   * Signed-out only; {@link renderSignInBanner} keeps the signed-in status.
   */
  private renderSignInCta(): TemplateResult | typeof nothing {
    if (this.signedInName) return nothing;
    return html`<div
      class="signin-cta"
      @click=${(e: Event) => e.stopPropagation()}
    >
      <span class="signin-cta-t">Have a membership or class pack?</span>
      <button
        type="button"
        class="zui-btn-secondary"
        title="Sign in to use your credits — don't pay twice"
        @click=${() => this.openSignInModal()}
      >
        ${ICON_SIGNIN} Sign in
      </button>
    </div>`;
  }

  /**
   * Signed-in status panel: who you are plus the credits this booking can draw
   * on. Signed-out visitors get {@link renderSignInCta} on the step-1 headline
   * row instead of a second banner.
   */
  private renderSignInBanner(): TemplateResult | typeof nothing {
    if (this.signedInName) {
      const credits = this.memberCtx?.credits ?? [];
      const creditLine = credits.length
        ? credits
            .map((c) =>
              c.remaining === null
                ? c.label
                : `${c.label} · ${c.remaining} left`,
            )
            .join(" · ")
        : "";
      return html`<div class="signin-banner">
        <span class="ic">${ICON_SIGNIN}</span>
        <div class="banner-copy">
          <div class="t">Welcome back, ${this.signedInName}</div>
          ${creditLine ? html`<div class="s">${creditLine}</div>` : nothing}
        </div>
        <button type="button" class="logout-link" @click=${() => this.logout()}>
          Log out
        </button>
      </div>`;
    }
    return nothing;
  }

  private renderStep(
    step: number,
    title: string,
    displayNum: number,
  ): TemplateResult {
    const open = this.activeStep === step;
    const isDone = this.done.has(step);
    // PLAT-6800: payment options are browsable before details are complete.
    const alwaysOpenable = step === 3;
    // PLAT-7041: opening payment early skips Next, so an earlier step can be
    // left without ever being done. It must still be reachable to finish it.
    const steps = this.visibleSteps;
    const beforeActive = steps.indexOf(step) < steps.indexOf(this.activeStep);
    const editable = isDone || beforeActive;
    return html`
      <div class="card step ${open ? "open" : ""}" data-step=${step}>
        <div
          class="step-head"
          @click=${() =>
            editable || open || alwaysOpenable
              ? this.setActiveStep(step)
              : null}
        >
          <span
            class="num ${open ? "open" : ""} ${isDone && !open ? "done" : ""}"
            >${isDone && !open ? ICON_CHECK : displayNum}</span
          >
          <span class="step-title">${title}</span>
          ${step === 1 && open ? this.renderSignInCta() : nothing}
          ${
            editable && !open && !this.chargeCompleted
              ? html`<button
                  class="edit"
                  @click=${(event: Event) => {
                    event.stopPropagation();
                    this.setActiveStep(step);
                  }}
                >
                  Edit
                </button>`
              : alwaysOpenable && !open && !this.chargeCompleted
                ? html`<button
                    class="edit"
                    @click=${(event: Event) => {
                      event.stopPropagation();
                      this.setActiveStep(step);
                    }}
                  >
                    View options
                  </button>`
                : nothing
          }
        </div>
        ${
          isDone && !open
            ? html`<div class="step-sum">${this.stepSummary(step)}</div>`
            : nothing
        }
        ${
          open
            ? html`<div class="body">${this.renderStepBody(step)}</div>`
            : nothing
        }
      </div>
    `;
  }

  private stepSummary(step: number): string {
    if (step === 1) {
      const name = `${this.buyer.firstName} ${this.buyer.lastName}`.trim();
      return [name, this.buyer.email].filter(Boolean).join(" · ");
    }
    if (step === 2)
      return this.attendees()
        .map(({ a }) => a.seat)
        .filter(Boolean)
        .join(", ");
    if (step === 3)
      return this.creditCovered && this.total === 0
        ? "Credit used"
        : `${formatShortAmount(this.subtotal, this.currency)} subtotal`;
    return "";
  }

  private renderStepBody(step: number): TemplateResult {
    if (step === 1) return this.renderDetails();
    if (step === 2) return this.renderSeats();
    if (step === 3) return this.renderPayChoice();
    return this.renderPayment();
  }

  /**
   * 3b: an IME composition (Japanese/Chinese/Korean candidate entry) is in
   * flight in one of the fields below. Not actor-backed — flipping it must not
   * itself schedule a render.
   */
  private composing = false;

  /**
   * The org-required profile fields, rendered inside step 1 next to the buyer's
   * own details. Guests only — a member's roster already carries this data.
   */
  private renderRequiredProfileFields(): TemplateResult | typeof nothing {
    if (!this.isGuestCheckout) return nothing;
    const required = resolveRequiredSignupFields(this.requiredSignupFields);
    const dobError = this.requiredProfile.dateOfBirth
      ? dateOfBirthError(this.requiredSignupFields, this.requiredProfile.dateOfBirth)
      : null;
    if (!required.dateOfBirth && !required.emergencyContact) return nothing;
    const patch = (field: keyof typeof this.requiredProfile, value: string) => {
      this.requiredProfile = { ...this.requiredProfile, [field]: value };
    };
    return html`
      ${
        required.dateOfBirth
          ? this.field(
              "Date of birth",
              this.requiredProfile.dateOfBirth,
              "date",
              (value) => patch("dateOfBirth", value),
              true,
            )
          : nothing
      }
      ${dobError ? html`<p role="alert" style="color:#b91c1c;font-size:0.8rem">${dobError}</p>` : nothing}
      ${
        required.emergencyContact
          ? html`<div class="grid2">
              ${this.field(
              "Emergency contact name",
              this.requiredProfile.emergencyContactName,
              "text",
              (value) => patch("emergencyContactName", value),
              true,
            )}
              ${this.field(
              "Emergency contact phone",
              this.requiredProfile.emergencyContactPhone,
              "tel",
              (value) => patch("emergencyContactPhone", value),
              true,
            )}
            </div>`
          : nothing
      }
    `;
  }

  private field(
    label: string,
    value: string,
    type: string,
    on: (v: string) => void,
    required = false,
  ): TemplateResult {
    // B1: `.value` uses `live()`, not a plain binding — formatPhoneInput()
    // truncates past the 10-digit cap, so the recomputed string equals the
    // previously-rendered one and lit's own dirty check would skip the write,
    // leaving the raw keystrokes visible in the DOM while state is capped.
    //
    // 3b: that same re-sync destroys an in-progress IME composition — lit
    // re-renders the WHOLE template on any reactive change, so a keystroke in
    // one field makes live() overwrite the half-composed candidate text in
    // another. Both halves of the round trip stand down while the IME owns the
    // element: `noChange` leaves the DOM property untouched, and the `@input`
    // guard keeps the intermediate composition buffer out of state (browsers
    // fire `input` with `isComposing: true` for every candidate update, and
    // letting those through would make even a plain, non-`live` binding write
    // the buffer straight back into the element).
    return html`<label class="fld"
      >${label}${required ? " *" : ""}
      <input
        type=${type}
        ?required=${required}
        .value=${this.composing ? noChange : live(value)}
        @compositionstart=${() => (this.composing = true)}
        @compositionend=${(e: Event) => this.endComposition(e, on)}
        @input=${(e: Event) => {
          if ((e as InputEvent).isComposing) return;
          on((e.target as HTMLInputElement).value);
        }}
      />
    </label>`;
  }

  /**
   * 3b: commit the composed text once the IME hands the element back. `input`
   * ordering around `compositionend` is browser-dependent (Chrome fires it
   * after, Safari before), so read the value straight off the element instead
   * of waiting for a follow-up `input`. The explicit `requestUpdate()`
   * guarantees one render with `live()` back in play even when the composed
   * text happens to equal what state already held, re-syncing any field whose
   * write was skipped while composing.
   */
  private endComposition(e: Event, on: (v: string) => void): void {
    this.composing = false;
    on((e.target as HTMLInputElement).value);
    this.requestUpdate();
  }

  private renderDetails(): TemplateResult {
    const isGuest = this.recipientType === "guest";
    return renderBookingDetails({
      firstNameField: this.field(
        "First name",
        this.buyer.firstName,
        "text",
        (value) => this.patchBuyer("firstName", value),
        true,
      ),
      lastNameField: this.field(
        "Last name",
        this.buyer.lastName,
        "text",
        (value) => this.patchBuyer("lastName", value),
        true,
      ),
      emailField: this.field(
        "Email",
        this.buyer.email,
        "email",
        (value) => this.patchBuyer("email", value),
        true,
      ),
      phoneField: this.field(
        "Phone",
        this.buyer.phone,
        "tel",
        (value) => this.patchBuyer("phone", value),
        this.phoneRequired,
      ),
      requiredProfileFields: this.renderRequiredProfileFields(),
      recipientSection: this.hideRecipientBooking
        ? nothing
        : this.renderOthers(isGuest),
      waiver:
        this.buyerAttends && this.hasWaiverRequirement
          ? this.renderWaiver()
          : nothing,
      intakeForms: this.renderIntakeForms(),
      errors: this.renderStep1Errors(),
      nextAction: this.renderNext(1),
    });
  }

  /**
   * 2b: normally the step-1 error list waits for `step1Touched`, which is only
   * set by the Next button's click handler — but that button is
   * `?disabled=${!valid}` and a disabled button fires no click. For a forms
   * load failure that is exactly the state the visitor is stuck in, so show
   * the list unprompted in that case and attach the retry.
   */
  private get step1ErrorsVisible(): boolean {
    return (
      (this.step1Touched || this.formsError) && this.step1Errors().length > 0
    );
  }

  private renderStep1Errors(): TemplateResult | typeof nothing {
    if (!this.step1ErrorsVisible) return nothing;
    return html`<div class="err">
      ${this.step1Errors().map((e) => html`<div>${e}</div>`)}
      ${
        this.formsError
          ? html`<button class="add-link" @click=${() => this.retryForms()}>
              Try again
            </button>`
          : nothing
      }
    </div>`;
  }

  private renderOthers(isGuest: boolean): TemplateResult {
    return renderOtherAttendees({
      isGuest,
      forSomeoneElse: this.forSomeoneElse,
      hasRecipientType: !!this.recipientType,
      includeSelf: this.includeSelf,
      isAppointment: this.isAppt,
      totalCapacity: this.totalCapacity,
      count: this.people.length,
      recipientModal: this.renderRecipientModal(),
      attendeeForms: this.people.map((person, index) =>
        this.renderPersonFields(person, index, isGuest),
      ),
      onToggleSomeoneElse: () => this.toggleSomeoneElse(),
      onChangeRecipientType: () => (this.recipientModalOpen = true),
      onToggleIncludeSelf: () => (this.includeSelf = !this.includeSelf),
      onSetCount: (count) => this.setCount(count),
    });
  }

  private renderPersonFields(
    person: Attendee,
    index: number,
    isGuest: boolean,
  ): TemplateResult {
    const help = isGuest
      ? "They'll sign the liability waiver at check-in and get their own account for next time. Add an email to send their confirmation."
      : "We'll save them to your account so you can book for them with one tap next time. Add an email to send their confirmation.";
    return renderAttendeeFields({
      heading: `${isGuest ? "Guest" : "Family member"} ${index + 1}`,
      removable: this.people.length > 1,
      firstNameField: this.field(
        "First name",
        person.firstName,
        "text",
        (value) => this.patchPerson(index, "firstName", value),
      ),
      lastNameField: this.field("Last name", person.lastName, "text", (value) =>
        this.patchPerson(index, "lastName", value),
      ),
      dateOfBirthField: isGuest
        ? nothing
        : this.field("Date of birth", person.dob, "date", (value) =>
            this.patchPerson(index, "dob", value),
          ),
      emailField: this.field(
        this.attendeeFieldRequired("email")
          ? "Email"
          : "Email (optional)",
        person.email,
        "email",
        (value) => this.patchPerson(index, "email", value),
        this.attendeeFieldRequired("email"),
      ),
      phoneField: this.field(
        this.attendeeFieldRequired("phone-number")
          ? "Phone"
          : "Phone (optional)",
        person.phone,
        "tel",
        (value) => this.patchPerson(index, "phone", value),
        this.attendeeFieldRequired("phone-number"),
      ),
      formFields: this.renderAttendeeForms(person, index),
      waiver: isGuest ? nothing : this.renderFamilyWaiver(index),
      help,
      onRemove: () => this.removePerson(index),
    });
  }

  /**
   * CK4: a "sign the waiver on behalf of this family member" toggle rendered
   * below the family member's contact fields (family branch only — guests sign
   * their own waiver at check-in, so they never get this toggle). Only shown
   * when this grouping actually requires a waiver. Flipping it on surfaces the
   * same liability-waiver copy the buyer signs (see WAIVER_PARAS) so it can be
   * agreed on the member's behalf.
   */
  private renderFamilyWaiver(i: number): TemplateResult | typeof nothing {
    if (!this.hasWaiverRequirement) return nothing;
    const waiver = this.familyWaiver[i] ?? {
      on: false,
      open: false,
      agreed: false,
    };
    const update = (patch: Partial<typeof waiver>) => {
      this.familyWaiver = {
        ...this.familyWaiver,
        [i]: { ...waiver, ...patch },
      };
    };
    return renderFamilyWaiverView({
      enabled: waiver.on,
      open: waiver.open,
      agreed: waiver.agreed,
      paragraphs: this.waiverBodyParas(this.waiverForm),
      onToggleEnabled: () => update({ on: !waiver.on }),
      onToggleOpen: () => update({ open: !waiver.open }),
      onToggleAgreed: (agreed) => update({ agreed }),
    });
  }

  private removePerson(i: number): void {
    if (this.people.length <= 1) return;
    this.people = this.people.filter((_, idx) => idx !== i);
    this.remapAttendeeAnswers((idx) =>
      idx === i ? null : idx > i ? idx - 1 : idx,
    );
  }

  /**
   * Move each extra attendee's typed form answers with them when the list
   * changes, dropping the ones whose person is gone.
   *
   * Their answers are keyed by position (see `answerKey`), so removing the
   * first of three people would otherwise hand their DOB to the person who
   * took their slot, and re-adding a row would resurrect answers for someone
   * who was never asked. `null` means "this person is gone".
   */
  private remapAttendeeAnswers(mapIndex: (i: number) => number | null): void {
    const remapKey = (key: string): string | null => {
      const match = /^p(\d+):(.*)$/.exec(key);
      if (!match) return key;
      const next = mapIndex(Number(match[1]));
      return next === null ? null : `p${next}:${match[2]}`;
    };
    const answers: Record<string, string> = {};
    for (const [key, value] of Object.entries(this.intakeAnswers)) {
      const next = remapKey(key);
      if (next !== null) answers[next] = value;
    }
    this.intakeAnswers = answers;
    const touched = new Set<string>();
    for (const key of this.touchedFields) {
      const next = remapKey(key);
      if (next !== null) touched.add(next);
    }
    this.touchedFields = touched;
  }

  /**
   * The waiver copy the visitor actually has to read.
   *
   * Sources BOTH places an org can author it, in form order:
   *  - `content.details` — the form-level body (arrives here already flattened
   *    to plain text by `live.ts`);
   *  - each question's `detailsHtml` — where real waivers actually live. The
   *    form builder attaches the legal text to the signature question, so
   *    production waiver forms have an EMPTY `content.details` and several KB
   *    of rich text on `questions[].detailsHtml`. Reading only `details` is why
   *    the accordion opened onto nothing.
   *
   * The hardcoded studio copy stays a last-resort fallback for an attached form
   * that carries no body text at all, so the block is never empty. A form with
   * neither questions nor details never reaches here — `loadForms()` drops it,
   * which drops the block AND its gate.
   */
  private waiverBodyParas(f: EmbedForm | null): string[] {
    const questions: RichFormQuestion[] = f?.questions ?? [];
    const authored = [
      ...toParagraphs(f?.details),
      ...questions.flatMap((q) => toParagraphs(q.detailsHtml)),
    ];
    return authored.length ? authored : [...WAIVER_PARAS];
  }

  private renderWaiver(): TemplateResult | typeof nothing {
    if (this.waiverOnFile || !this.hasWaiverRequirement || !this.waiverForm) {
      return nothing;
    }
    const form = this.waiverForm;
    const questions = form.questions.filter(
      (question) => this.identityAnswerFor(question) === null,
    );
    return renderBookingWaiverView({
      name: form.name,
      open: this.waiverOpen,
      checked: this.waiverChecked,
      paragraphs: this.waiverBodyParas(form),
      fields: questions.map((question) =>
        this.renderIntakeField(form.id, question),
      ),
      error: this.intakeError,
      onToggleOpen: () => (this.waiverOpen = !this.waiverOpen),
      onToggleChecked: (checked) => (this.waiverChecked = checked),
    });
  }

  private renderSeats(): TemplateResult {
    return renderBookingSeats({
      attendees: this.attendees().map(({ a, label }) => ({
        attendee: a,
        label,
      })),
      activeAttendee: this.activeSpot,
      error: this.seatError,
      roomAvailability: this.roomAvail,
      nextAction: this.renderNext(2),
      onSelectAttendee: (index) => (this.activeSpot = index),
      onAssignGenericSeat: (seat) => this.assignSeat(seat),
      onSelectRealSpot: (spot) => void this.selectRealSpot(spot),
    });
  }

  private renderPayChoice(): TemplateResult {
    if (this.catalogPending) {
      return html`<div class="pay-group-sub" role="status">
          Loading payment options…
        </div>
        ${this.renderNext(3)}`;
    }
    return renderPaymentChoices({
      attendees: this.attendees().map(({ a, label, isBuyer }) => ({
        attendee: a,
        label,
        isBuyer,
        radioIndex: isBuyer ? -1 : this.extras.indexOf(a),
      })),
      tiers: this.tiers,
      currency: this.currency,
      tiersFor: (entry) =>
        this.tiersForAttendee(entry.isBuyer, entry.attendee.familyUserId),
      unavailableFor: (entry) =>
        this.paymentChoiceUnavailable(entry.attendee, entry.isBuyer),
      signedIn: !!this.signedInName,
      familyPackAvailable: this.familyPackAvailable,
      recipientType: this.recipientType,
      buyOptionsOpen: this.buyOptionsOpen,
      nextAction: this.renderNext(3),
      memberCreditsFor: (relationship) => this.memberCreditsFor(relationship),
      onSetTier: (entry, tierId) =>
        this.patchAttendee(
          { a: entry.attendee, isBuyer: entry.isBuyer },
          {
            tierId,
            memberCreditProductId: "",
            memberCreditPunchCardId: undefined,
            memberAccessProductId: entry.isBuyer
              ? this.ownedAccessProductIds.includes(tierId)
                ? tierId
                : ""
              : familyAccessProductId(
                  tierId,
                  entry.attendee.familyUserId,
                  this.familyAccess[entry.attendee.familyUserId]
                    ?.freeProductIds ?? [],
                ),
          },
        ),
      onSetMemberCredit: (entry, productId, punchCardId) =>
        this.patchAttendee(
          { a: entry.attendee, isBuyer: entry.isBuyer },
          {
            memberCreditProductId: productId,
            memberCreditPunchCardId: punchCardId,
            memberAccessProductId: "",
            useCredit: false,
          },
        ),
      onSetFamilyCredit: (entry, useCredit) =>
        this.patchAttendee(
          { a: entry.attendee, isBuyer: entry.isBuyer },
          { useCredit, memberCreditProductId: "", memberAccessProductId: "", memberCreditPunchCardId: undefined },
        ),
      onToggleBuyOptions: () => (this.buyOptionsOpen = !this.buyOptionsOpen),
    });
  }

  /** Attendees who are actually paying (skips free/shared-credit), in charge order. */
  private chargedAttendees(): { a: Attendee; label: string }[] {
    return checkoutMath.chargedAttendees(
      this.attendees(),
      this.tiers,
      this.familyPackAvailable,
    );
  }

  /** Charge line items, aligned 1:1 with chargedAttendees(). */
  /**
   * Response id for a product's prior form, from what this checkout just
   * submitted — the value `validateRequiredForms` checks before anything else.
   */
  private priorFormResponseFor(tierId: string): string | undefined {
    const formId = checkoutMath.findTier(this.tiers, tierId)?.priorFormId;
    if (!formId) return undefined;
    return this.productSubmissions.find((s) => s.formId === formId)
      ?.formResponseId;
  }

  /**
   * Attach each line's prior-form response, so the charge can see the form the
   * buyer completed moments ago rather than only what their roster already
   * owned. Applied here (rather than in `buildChargeItems`) because the ids do
   * not exist until `runBooking` has submitted, while items are built earlier
   * for pricing and re-synced afterwards by `syncPaymentItems`.
   */
  private withPriorFormResponses(items: ChargeItem[]): ChargeItem[] {
    if (!this.productSubmissions.length) return items;
    const charged = this.chargedAttendees();
    return items.map((item, i) => {
      const tierId = charged[i]?.a.tierId ?? this.buyer.tierId;
      const formResponseId = this.priorFormResponseFor(tierId);
      return formResponseId ? { ...item, formResponseId } : item;
    });
  }

  private chargeItems(): ChargeItem[] {
    return this.withPriorFormResponses(this.buildChargeItems());
  }

  /**
   * Tell the server which appointment type each line is for.
   *
   * The org's "pass fees to customer" switch lives on the appointment type,
   * while the line only names the drop-in product that type mints — and that
   * product carries no such flag. Sending the type is what lets the charge
   * preview quote the fee, and the charge itself collect it, for a service the
   * org configured to pass fees on. Same pair the in-app booking page sends.
   */
  private withAppointmentContext(items: ChargeItem[]): ChargeItem[] {
    if (!this.appointmentTypeId) return items;
    const charged = this.chargedAttendees();
    return items.map((item, index) => ({
      ...item,
      appointmentTypeId: this.appointmentTypeId,
      appointmentDate: this.startIso || undefined,
      ...(charged[index] ? { appointmentTargetUserId: charged[index].a === this.buyer ? this.memberCtx?.userId : charged[index].a.familyUserId || null } : {}),
      ...(this.appointmentProviderUserId
        ? { providerUserId: this.appointmentProviderUserId }
        : {}),
    }));
  }

  private buildChargeItems(): ChargeItem[] {
    if (this.isAppt) {
      if (this.tiers.length) {
        return this.withAppointmentContext(
          checkoutMath.buildChargeItems(
            this.chargedAttendees(),
            this.tiers,
            this.serviceName || "Appointment",
          ),
        );
      }
      // PLAT-5624: charge the server-resolved price, not the schedule feed's
      // `price` (0 for a credit-priced service — see effectiveServicePrice).
      const amount = this.effectiveServicePrice;
      if (amount <= 0) return [];
      const qty = this.attendees().length;
      return this.withAppointmentContext([
        {
          stripeProductId: this.chargeStripeProductId(),
          amount,
          quantity: qty,
          customDescription: this.serviceName || "Appointment",
        },
      ]);
    }
    if (!this.tiers.length) {
      if (this.price <= 0) return [];
      const charged = this.chargedAttendees();
      if (!charged.length) return [];
      return [
        {
          stripeProductId: this.chargeStripeProductId(),
          amount: this.price,
          quantity: charged.length,
          customDescription: this.className,
        },
      ];
    }
    return checkoutMath.buildChargeItems(
      this.chargedAttendees(),
      this.tiers,
      this.className,
    );
  }

  /**
   * Stripe product id for the current charge (appointment service or class tier).
   *
   * PLAT-5624: `serviceStripeProductId` comes from the public schedule feed and
   * is EMPTY until the appointment type's drop-in product has been minted
   * server-side, which is what made `<zipper-payment>` render "This item isn't
   * available for online purchase yet." for every appointment nobody had ever
   * bought through the dashboard. `resolvedServiceProductId` is the id the
   * pay-options lookup returns (creating it if needed), so it is the reliable
   * fallback.
   */
  private appointmentProductId(): string {
    return this.serviceStripeProductId || this.resolvedServiceProductId || "";
  }

  private chargeStripeProductId(): string {
    if (this.isAppt) {
      if (this.tiers.length) {
        const charged = this.chargedAttendees()[0];
        if (!charged) return this.appointmentProductId();
        const tier = checkoutMath.findTier(this.tiers, charged.a.tierId);
        return tier.stripeProductId || tier.id || this.appointmentProductId();
      }
      return this.appointmentProductId();
    }
    const charged = this.chargedAttendees()[0];
    if (!charged) return "";
    const tier = checkoutMath.findTier(this.tiers, charged.a.tierId);
    return tier.stripeProductId || tier.id || "";
  }

  /**
   * After a successful charge, register each paid attendee for the class (and
   * confirm their held seat via spotHoldId). Only runs when live registration
   * context is present; mock/playground bookings skip it.
   */
  private async formsForAttendee(att: {
    a: Attendee;
    isBuyer: boolean;
  }): Promise<{ formId: number; formResponseId: string }[] | undefined> {
    if (att.isBuyer) {
      return this.intakeSubmissions.length ? this.intakeSubmissions : undefined;
    }
    const subs: { formId: number; formResponseId: string }[] = [];
    const forms = this.attendeeRequiredForms();
    if (!forms.length) return undefined;
    // Where this attendee's own typed answers live. `people` holds the exact
    // objects `attendees()` labels, so identity is the index — and a person
    // who somehow isn't in the list still submits (identity + consent only)
    // rather than silently losing their booking.
    const index = this.people.indexOf(att.a);
    const person = index >= 0 ? { index, a: att.a } : undefined;
    // Saved family members already have an account. Their guardian's embedded
    // session can save a response for that dependent, and the API authorizes
    // the relationship. Everyone else needs a guest capability tied to the
    // roster created for this attendee.
    const roster = att.a.familyUserId
      ? null
      : await this.ensureAttendeeRoster(att.a);
    const identity: FormSubmitIdentity | undefined = att.a.familyUserId
      ? { kind: "member", targetUserId: att.a.familyUserId }
      : roster
        ? {
            kind: "guest",
            rosterId: roster.rosterId,
            checkoutToken: roster.checkoutToken,
          }
        : undefined;
    for (const f of forms) {
      const answers = f.questions
        .map((q) => ({
          questionKey: q.key,
          // The attendee's own answer, by the same resolution the step-1 gate
          // validated: their contact fields for the identity questions, what
          // the booker typed into their card for everything else, and the
          // recorded consent for the signature/free-text ones. Previously only
          // the identity questions had a value at all, so any other required
          // question went up blank and the API refused the whole response.
          value: person
            ? this.submitValueFor(f.id, q, person) ||
              (this.isConsentQuestion(q) ? "Agreed" : "")
            : (this.identityAnswerFor(q, att.a)?.trim() ??
              (this.isConsentQuestion(q) ? "Agreed" : "")),
        }))
        .filter((a) => a.value);
      if (!answers.length) continue;
      const id = await submitAppointmentForm(
        this.orgId,
        f.id,
        answers,
        identity,
      );
      if (!id) throw new Error("Could not save guest details");
      subs.push({ formId: f.id, formResponseId: id });
    }
    return subs.length ? subs : undefined;
  }

  private async registerAfterCharge(
    invoiceIds: (string | undefined)[],
    completedAttendees = new Set<Attendee>(),
  ): Promise<void> {
    if (!this.orgSlug || !this.eventSlug || !this.activityId) return;

    const omitBookerWaiver =
      this.waiverOnFile || (!this.buyerAttends && this.hasWaiverRequirement);

    let buyerFormsAttached = false;
    const takeBuyerForms = () => {
      if (buyerFormsAttached) return undefined;
      buyerFormsAttached = true;
      return this.intakeSubmissions.length ? this.intakeSubmissions : undefined;
    };

    const registerOne = async (
      att: { a: Attendee; label: string; isBuyer: boolean },
      stripeInvoiceId?: string,
      consumeCreditForProductId?: string,
    ) => {
      if (completedAttendees.has(att.a)) return;
      const formSubmissions = att.isBuyer
        ? takeBuyerForms()
        : await this.formsForAttendee(att);
      // The extra attendee's OWN roster, so the seat is booked for them rather
      // than collapsing onto the purchaser. Find-or-reuse: `formsForAttendee`
      // has usually already minted it (it needs one to file their forms), and a
      // second `create-guest` for the same email 401s. The buyer and saved
      // family members go through the authenticated routes, which name the
      // attendee themselves.
      const attendeeRoster =
        att.isBuyer || att.a.familyUserId
          ? null
          : await this.ensureAttendeeRoster(att.a);
      const body = {
        activityId: this.activityId,
        stripeInvoiceId,
        spotHoldId: att.a.holdId || undefined,
        formSubmissions,
        omitBookerWaiver:
          att.isBuyer || att.a.familyUserId ? omitBookerWaiver : false,
        purchasedForRosterId:
          attendeeRoster?.rosterId ??
          (consumeCreditForProductId
            ? att.a.familyRosterId || this.memberCtx?.rosterId
            : undefined),
        // Whose pack pays for it. Naming the attendee above means the credit
        // holder is no longer implied by the same field: this seat belongs to
        // the person attending, and the credit comes out of the signed-in
        // buyer's pack (the API requires their own bearer token to spend it,
        // which this request carries).
        consumeCreditFromRosterId:
          consumeCreditForProductId && attendeeRoster
            ? this.memberCtx?.rosterId
            : undefined,
        consumeCreditForProductId,
        accessedWithProductId: att.a.memberAccessProductId || undefined,
      };
      if (att.a.familyUserId && this.memberCtx) {
        await registerFamilyMember(
          this.orgId,
          this.orgSlug,
          this.eventSlug,
          {
            ...body,
            targetUserId: att.a.familyUserId,
            punchCardId: consumeCreditForProductId ? att.a.memberCreditPunchCardId : undefined,
            punchCardUserId: consumeCreditForProductId && att.a.memberCreditPunchCardId
              ? this.memberCtx.userId : undefined,
          },
          ...runtimeApiArguments(this.runtime),
        );
        completedAttendees.add(att.a);
        return;
      }
      if (att.isBuyer && this.memberCtx) {
        await registerSelf(
          this.orgId,
          this.orgSlug,
          this.eventSlug,
          { ...body, punchCardId: consumeCreditForProductId ? att.a.memberCreditPunchCardId : undefined },
          ...runtimeApiArguments(this.runtime),
        );
        completedAttendees.add(att.a);
        return;
      }
      await registerGuest(
        this.orgId,
        this.orgSlug,
        this.eventSlug,
        { ...body, consumeCreditFromPunchCardId: consumeCreditForProductId ? att.a.memberCreditPunchCardId : undefined },
        ...runtimeApiArguments(this.runtime),
      );
      completedAttendees.add(att.a);
    };

    const chargedList = this.chargedAttendees();
    const chargedEntries = this.attendees().filter(({ a }) =>
      chargedList.some((c) => c.a === a),
    );
    for (let i = 0; i < chargedEntries.length; i++) {
      await registerOne(chargedEntries[i], invoiceIds[i]);
    }

    // PLAT-6791: All Access attendees were never charged; register them on
    // their pack (`accessedWithProductId`), with no invoice.
    const accessCovered = checkoutMath.accessCoveredAttendees(this.attendees());
    for (const att of this.attendees()) {
      if (accessCovered.some((c) => c.a === att.a)) await registerOne(att);
    }

    const covered = checkoutMath.creditCoveredAttendees(this.attendees());
    for (const { att, productId } of covered) {
      const full = this.attendees().find(({ a }) => a === att.a) ?? {
        ...att,
        isBuyer: att.a === this.buyer,
      };
      await registerOne(full, undefined, productId);
    }
  }

  private renderPayment(): TemplateResult {
    const needsTerms = !!this.termsForm;
    return html`
      ${this.renderTerms()}
      ${
        !this.classPricingUnresolved &&
        this.chargeItems().length > 0 &&
        (this.total > 0 || !!this.deferredSelection) &&
        !this.deferredSelection?.tier?.deferredMembershipPurchaseId
          ? html`<div id="inline-pay-host" class="inline-pay"></div>`
          : nothing
      }
      ${
        this.bookingError
          ? html`<div class="err">${this.bookingError}</div>`
          : nothing
      }
      ${
        // PLAT-5624: explain the disabled CTA rather than showing a dead
        // "Book appointment · Free" for a service we have no price for.
        this.apptPricingUnresolved && !this.bookingError
          ? html`<div class="err" role="status">
              ${
                this.apptPayError ||
                "Checking this appointment’s price and payment options…"
              }
            </div>`
          : nothing
      }
      ${this.deferredSelection?.tier?.deferredMembershipPurchaseId ? html`<div role="status"><p>${this.deferredBookingAccess
        ? this.deferredBookingAccess.triggerType === 'first_booking'
          ? `Confirming this booking triggers your first membership charge immediately: ${formatCents(this.deferredBookingAccess.futureChargeAmountInCents, this.currency)}.`
          : `Due today: ${formatCents(0, this.currency)}. First charge ${this.deferredBookingAccess.expectedChargeLabel}: ${formatCents(this.deferredBookingAccess.futureChargeAmountInCents, this.currency)}.`
        : 'Review your reserved membership before confirming this booking.'}</p>${this.deferredBookingAccess ? html`<label><input type="checkbox" .checked=${this.existingDeferredConsent} @change=${(e: Event) => { this.existingDeferredConsent = (e.target as HTMLInputElement).checked; this.requestUpdate(); }} /> I understand when my membership will be charged.</label>` : nothing}</div>` : nothing}
      ${this.classPricingUnresolved ? html`<div class="err" role="status">
        ${this.classPayError || "Checking this class’s payment options…"}
        ${this.classPayError ? html`<button class="zui-link" @click=${() => void this.retryClassPayment()}>
          Try again
        </button>` : nothing}
      </div>` : nothing}
      ${this.chargeCompleted ? nothing : html`<button
        class="zui-cta"
        style="margin-top:0.6rem"
        ?disabled=${
          (needsTerms && !this.termsValid) ||
          this.submitting ||
          this.chargeCompleted ||
          // PLAT-5624: never offer a $0 booking for an appointment whose price
          // we could not resolve — that is the free-booking revenue leak.
          this.apptPricingUnresolved ||
          this.classPricingUnresolved ||
          (this.chargeItems().length > 0 &&
            (this.total > 0 || !!this.deferredSelection) &&
            !this.deferredSelection?.tier?.deferredMembershipPurchaseId &&
            !this.payReady)
        }
        aria-busy=${this.submitting ? "true" : "false"}
        @click=${() => void this.bookClass()}
      >
        ${
          this.submitting
            ? "Processing…"
            : this.chargeCompleted
              ? "Payment received"
              : this.apptPricingUnresolved || this.classPricingUnresolved
                ? "Checking price…"
                : this.creditCovered && this.total <= 0 && this.chargeItems().length === 0 && !this.deferredSelection?.tier?.deferredMembershipPurchaseId
                  ? "Confirm booking · Credit used"
                : this.deferredSelection
                  ? this.deferredSelection.tier?.deferredMembershipPurchaseId
                    ? this.deferredBookingAccess ? 'Confirm membership booking' : 'Review membership booking'
                    : this.deferredSelection.tier?.deferredChargeSettings?.triggerType === 'first_booking' ? `Authorize & book · ${this.amountDue}` : `Save card & book · ${formatShortAmount(0, this.currency)} today`
                  : this.total > 0
                  ? this.isAppt
                    ? `Book & pay · ${this.amountDue}`
                    : `${this.bookButtonText} · ${this.amountDue}`
                  : this.creditCovered
                    ? "Confirm booking · Credit used"
                    : this.isAppt
                      ? "Book appointment · Free"
                      : "Confirm booking · Free"
        }
      </button>`}
    `;
  }

  private renderTerms(): TemplateResult | typeof nothing {
    const forms = this.cancellationForms();
    if (!forms.length) return nothing;
    // Same authoring split as the waiver: the terms text may live on the
    // form's `details` OR on a question's `detailsHtml`. No hardcoded
    // fallback here — there is no generic wording to stand in for an org's
    // own cancellation policy.
    return html`${forms.map((f) => {
      const onFile = !this.formNeedsResponse(f.id);
      const complete = this.cancellationComplete(f);
      const bodyParas = [
        ...toParagraphs(f.details),
        ...f.questions.flatMap((q) => toParagraphs(q.detailsHtml)),
      ];
      return html`
        <div class="waiver terms">
          <button
            class="waiver-head"
            @click=${() => (this.termsOpen = !this.termsOpen)}
          >
            <span class="ic">${ICON_DOC}</span>
            <span style="flex:1;min-width:0">
              <span class="t" style="display:block"
                >${f.name || "Purchase terms & conditions"}</span
              >
              <span class="s"
                >${onFile
                  ? "Already on file — tap to review."
                  : complete
                    ? "Complete — tap to review."
                    : "Required — complete the fields below."}</span
              >
            </span>
            <span class="chev ${this.termsOpen ? "open" : ""}"
              >${ICON_CHEVRON}</span
            >
          </button>
          ${this.termsOpen
            ? html`<div class="waiver-body">
                ${bodyParas.map((p) => html`<p>${p}</p>`)}
                ${onFile
                  ? html`<p>This form is already on file.</p>
                      ${f.questions.map((q) => html`<p>${q.label}</p>`)}`
                  : html`<div class="waiver-fields">
                      ${f.questions.map((q) => this.renderIntakeField(f.id, q))}
                    </div>`}
              </div>`
            : nothing}
          ${!onFile && !f.questions.length
            ? html`<label class="waiver-check">
                <input
                  type="checkbox"
                  .checked=${complete}
                  @change=${(e: Event) => {
                    const checked = (e.target as HTMLInputElement).checked;
                    this.termsConsents = { ...this.termsConsents, [f.id]: checked };
                    if (forms.length === 1) this.termsChecked = checked;
                  }}
                />
                I agree to the purchase terms and conditions above.
              </label>`
            : nothing}
        </div>
      `;
    })}`;
  }

  private openPayment(): void {
    if (this.classPricingUnresolved) return;
    // PLAT-5624: idempotent. `setActiveStep(4)` schedules this behind an
    // `await`, checking `!this.paymentEl` BEFORE that await — so anything that
    // mounts one in the gap (see `updated()`) would otherwise end up with a
    // SECOND live card form, i.e. two independently chargeable sessions. Each
    // element owns its own submission actor, so a duplicate element defeats
    // the single-flight guard entirely.
    if (this.paymentEl) return;
    const el = document.createElement("zipper-payment") as ZipperPayment;
    el.runtime = this.runtime;
    el.inline = true;
    el.stripe = this.stripe;
    el.orgId = this.orgId;
    el.accent = this.accent;
    el.requirePhone = this.requirePhone;
    // The payment element re-derives its own phone/profile gates from this, but
    // step 1 has already collected them — a guest reaching the card form has
    // satisfied the policy, and `guestRoster` below usually means the roster is
    // already minted with the data.
    el.requiredSignupFields = this.requiredSignupFields;
    el.currency = this.currency;
    el.heading = `Pay for ${this.isAppt ? this.serviceName || "your appointment" : this.className}`;
    el.items = this.chargeItems();
    el.bookingCheckout = true;
    el.bookingPrerequisites = this.buildBookingPrerequisites();
    el.purchasedForRosterId = this.deferredSelection?.att.a.familyRosterId || this.memberCtx?.rosterId || 0;
    el.afterDeferredPurchase = async (purchase) => {
      try { await this.finishMembershipBooking(purchase); }
      catch (e) { throw new Error(`Your membership is reserved, but booking could not be completed: ${e instanceof Error ? e.message : 'Please try again.'} Retry this booking to reuse your reservation.`); }
    };
    el.customer = {
      firstName: this.buyer.firstName,
      lastName: this.buyer.lastName,
      email: this.buyer.email,
      phone: this.buyer.phone,
      // The org-required fields collected on step 1. This element mints the
      // guest roster itself whenever `runBooking()` had no forms to submit
      // (`ensureGuestRoster` runs only for those), so without these the
      // create-guest it fires carries no date of birth and the API rejects it.
      ...requiredProfilePayload(this.requiredProfile),
    };
    el.couponId = this.appliedCouponId;
    el.discount = this.discount;
    // Phase 4 (additive): offer the member's saved cards. Empty for guests, so
    // the payment modal behaves exactly as before.
    el.savedCards = this.memberCtx?.savedCards ?? [];
    el.memberContext = this.memberCtx ?? undefined;
    // Reuse the roster the intake forms were filed against, rather than letting
    // the charge mint a second one for the same email (which 401s).
    el.guestRoster = this.guestRoster ?? undefined;
    // A wallet charge starts from Stripe's button, not the CTA, so it has to
    // file the waiver/intake forms itself before any money moves.
    el.beforeCharge = async () => {
      if (this.bookingPrepared) return;
      if (!(await this.prepareBooking())) {
        const message =
          this.bookingError ||
            this.intakeError ||
            "Complete your booking details before paying.";
        this.bookingError = "";
        throw new Error(message);
      }
      this.bookingError = "";
      // Product prior-form responses go on the charge lines.
      this.syncPaymentItems();
    };
    const completedAttendees = new Set<Attendee>();
    el.afterCharge = (invoiceIds) =>
      this.registerAfterCharge(invoiceIds, completedAttendees);
    el.addEventListener("zipper-pay-committed", () => {
      this.submission.markPaymentCommitted();
    });
    el.addEventListener("zipper-pay-recovery", () => {
      this.submission.fail("Confirmation incomplete", true);
    });
    // The payment step previews the charge; mirror its numbers in the summary
    // rather than previewing the same cart a second time.
    el.addEventListener("zipper-pay-quote", (e: Event) => {
      // Detached/old payment elements and a now-free cart cannot restore an
      // obsolete quote after an attendee or pricing change.
      if (
        this.paymentEl !== el ||
        this.total <= 0 ||
        JSON.stringify(el.items) !== JSON.stringify(this.chargeItems()) ||
        el.couponId !== this.appliedCouponId
      )
        return;
      this.quote =
        (e as CustomEvent<{ totals: OrderTotals | null }>).detail?.totals ??
        null;
    });
    el.addEventListener("zipper-pay-success", (e: Event) => {
      const detail = (e as CustomEvent).detail as
        { invoiceIds?: (string | undefined)[]; deferredPurchase?: DeferredMembershipPurchaseDto } | undefined;
      this.placedOrderId = detail?.deferredPurchase?.id ?? detail?.invoiceIds?.[0] ?? this.placedOrderId;
      // PLAT-5624: mark the charge done synchronously — everything below is
      // async, and the CTA must not re-enable in the gap.
      this.submission.markPaymentCommitted();
      this.teardownPayment();
      void this.syncMemberPhone().then(() => this.placeOrder());
    });
    el.addEventListener("zipper-pay-close", () => this.teardownPayment());
    // PLAT-5624: "Try again" on the not-purchasable panel. `payable` is derived
    // from `items`, so re-resolve the appointment's pay options and hand over a
    // fresh set — the usual cause is a Stripe product id that hadn't been
    // resolved yet when the payment step mounted.
    el.addEventListener("zipper-pay-retry", () => {
      void (this.isAppt ? this.loadAppointmentPayContext() : this.retryClassPayment())
        .then(() => this.syncPaymentItems());
      this.syncPaymentItems();
    });
    // PLAT-5624: keep the outer CTA inert for charges this component did not
    // start itself (the Apple/Google Pay sheet submits from Stripe's button).
    el.addEventListener("zipper-pay-busy", (e: Event) => {
      const detail = (e as CustomEvent<{ busy: boolean }>).detail;
      this.submission.setChildBusy(!!detail?.busy && !this.placed);
    });
    const host =
      this.renderRoot.querySelector("#inline-pay-host") ?? document.body;
    // Must be attached BEFORE appendChild: the component fires its first
    // zipper-pay-ready(false) on its first update.
    el.addEventListener("zipper-pay-ready", (e) => {
      this.payReady = (
        e as CustomEvent<{ canSubmit: boolean }>
      ).detail.canSubmit;
    });
    host.appendChild(el);
    this.paymentEl = el;
  }

  /**
   * PLAT-5624: push the CURRENT charge items at a mounted `<zipper-payment>`.
   *
   * `openPayment()` copies `chargeItems()` once, at mount. Appointment pricing
   * resolves asynchronously (see `loadAppointmentPayContext`), so a payment
   * step that mounted first would otherwise hold the pre-resolution items —
   * i.e. an item with an empty `stripeProductId`, which is exactly the state
   * that renders "This item isn't available for online purchase yet." and never
   * recovers. Keeping them in sync makes that panel self-heal.
   */
  private syncPaymentItems(): void {
    const el = this.paymentEl;
    if (!el) return;
    const items = this.chargeItems();
    const prerequisites = this.buildBookingPrerequisites();
    if (el.bookingPrerequisites?.key !== prerequisites.key || el.bookingPrerequisites.ready !== prerequisites.ready)
      el.bookingPrerequisites = prerequisites;
    const customer = {
      firstName: this.buyer.firstName, lastName: this.buyer.lastName,
      email: this.buyer.email, phone: this.buyer.phone,
      ...requiredProfilePayload(this.requiredProfile),
    };
    if (JSON.stringify(el.customer) !== JSON.stringify(customer)) el.customer = customer;
    el.memberContext = this.memberCtx ?? undefined;
    el.couponId = this.appliedCouponId;
    el.discount = this.discount;
    el.purchasedForRosterId = this.deferredSelection?.att.a.familyRosterId || this.memberCtx?.rosterId || 0;
    const same =
      el.items.length === items.length &&
      el.items.every(
        (it, i) =>
          it.stripeProductId === items[i].stripeProductId &&
          it.amount === items[i].amount &&
          it.quantity === items[i].quantity &&
          // The payment step can mount BEFORE the forms are submitted (step 4
          // opens it), so the prior-form response id is often the only field
          // that changes afterwards. Omitting it here would leave the mounted
          // element charging without the response the buyer just gave.
          it.formResponseId === items[i].formResponseId &&
          it.appointmentDate === items[i].appointmentDate &&
          it.appointmentTargetUserId === items[i].appointmentTargetUserId,
      );
    if (same) return;
    el.items = items;
  }

  private buildBookingPrerequisites(): BookingPaymentPrerequisites {
    return {
      ready: (!this.classForms.length || this.formsResolved) && !this.productFormsPending &&
        !this.classPricingUnresolved && !this.apptPricingUnresolved &&
        this.stepValid(1) && this.termsValid && !this.payChoicePending,
      // Persistence outputs are excluded: saving answers and minting the guest
      // capability must not invalidate the preparation that produced them.
      key: JSON.stringify([
        this.orgId, this.orgSlug, this.eventSlug, this.activityId,
        this.appointmentTypeId, this.startIso, this.memberCtx?.rosterId ?? "guest",
        this.buyer, this.people, this.requiredProfile, this.classForms,
        this.waiverForm, this.intakeForms, this.productForms,
        [...this.formsOnFile], this.waiverOnFile, this.waiverChecked,
        this.intakeAnswers, this.termsChecked, this.termsConsents,
        this.chargeItems().map(({ formResponseId: _responseId, ...item }) => item),
        this.appliedCouponId, this.discount,
      ]),
    };
  }

  private teardownPayment(): void {
    this.paymentEl?.remove();
    this.paymentEl = null;
    this.payReady = false;
  }

  private renderNext(step: number): TemplateResult {
    const valid = this.stepValid(step);
    const isLast = step === this.visibleSteps[this.visibleSteps.length - 1];
    return html`
      ${
        !valid && step === 1 && !this.step1ErrorsVisible
          ? html`<div class="err">Complete required fields to continue.</div>`
          : nothing
      }
      ${
        !valid && step === 2
          ? html`<div class="err">Assign a spot for every attendee.</div>`
          : nothing
      }
      ${
        !valid && step === 3 && !this.catalogPending && !this.paymentOptionsUnavailable
          ? html`<div class="err">Choose a payment option to continue.</div>`
          : nothing
      }
      ${
        !isLast
          ? html`<button
              class="next"
              ?disabled=${!valid}
              @click=${() => this.goNext(step)}
            >
              Next
            </button>`
          : nothing
      }
    `;
  }

  private renderSummary(): TemplateResult {
    const items = this.lineItems();
    const count = this.attendees().length;
    // Fully covered by an owned credit-pack: it isn't a monied transaction, so
    // drop the price chrome (promo, subtotal/tax/total, SSL badge) and say
    // "Credit used" rather than "Free".
    const creditOnly = this.creditCovered && this.total === 0;
    return renderBookingSummary({
      items,
      attendeeCount: count,
      image: this.image,
      thumbInitials: this.thumbInitials,
      displayTitle: this.displayTitle,
      displayMetaParts: this.displayMetaParts,
      duration: this.duration,
      isAppointment: this.isAppt,
      appointmentLocation: this.apptLocation,
      appointmentPrice: this.price,
      creditOnly,
      deferred: !!this.deferredSelection,
      subtotal: this.subtotal,
      total: this.total,
      promoCode: this.promoCode,
      promoBusy: this.promoBusy,
      checkoutLocked: this.chargeCompleted,
      promoError: this.promoError,
      discount: this.discount,
      quote: this.quote ?? undefined,
      currency: this.currency,
      payChoicePending: this.payChoicePending,
      onChoosePayment: () => this.openPayChoice(),
      onPromoCodeChange: (value) => {
        this.promoCode = value;
        this.promoError = "";
      },
      onApplyPromo: () => void this.applyPromo(),
    });
  }

  private returnToSchedule(): void {
    // SC11: "Return to Schedule — Book another class" — leave checkout entirely
    // and drop the user back on the fresh schedule list (first screen). Guest
    // checkout now covers extra spots, so re-booking in place is redundant.
    // Closing dispatches zipper-booking-close; the parent schedule clears its
    // bookingClass and re-renders the class list, and a brand-new checkout
    // component is mounted next time so no in-place reset is needed.
    this.close();
  }

  private renderConfirmation(): TemplateResult {
    // The instructor belongs to the subline ("… with Chris Alto") only. Naming
    // them in the title too wrapped it onto two lines, and the confirmation
    // card's price sits to the right of the FIRST line — so "Baby Yoga with
    // Chris" / "Credit used" read as "Baby Yoga with Credit used Chris".
    const title = this.isAppt
      ? `${this.serviceName || "Appointment"}${this.duration ? ` · ${this.duration}` : ""}`
      : this.className;
    const subline = this.isAppt
      ? [this.apptWhen, this.providerName ? `with ${this.providerName}` : ""]
          .filter(Boolean)
          .join(" ")
      : `${this.classWhen}${this.instructorName ? ` with ${this.instructorName}` : ""}`;
    return renderBookingConfirmation({
      kind: this.kind,
      accent: this.accent,
      orgId: this.orgId,
      email: this.buyer.email,
      firstName: this.buyer.firstName,
      lastName: this.buyer.lastName,
      title,
      subline,
      price:
        this.deferredBookingAccess
          ? this.deferredBookingAccess.triggerType === 'first_booking'
            ? `First charge authorized: ${formatCents(this.deferredBookingAccess.futureChargeAmountInCents, this.currency)}`
            : `Due today: ${formatCents(0, this.currency)} · First charge ${this.deferredBookingAccess.expectedChargeLabel}`
          : this.total > 0
          ? formatShortAmount(this.total, this.currency)
          : this.creditCovered
            ? "Credit used"
            : "Free",
      orderId: this.placedOrderId,
      startIso: this.startIso,
      endIso: this.endIso,
      hideSetupAccount: !!this.memberCtx,
      onBookAnother: () => this.returnToSchedule(),
      onShare: () => this.shareBooking(),
      onAccountSetup: (firstName) => {
        this.signedInName = firstName || this.buyer.firstName || "there";
        void this.loadMember();
      },
      onSignInSetup: () =>
        openSignIn(this.orgId, this.accent, (name) => {
          this.signedInName = name || "there";
          void this.loadMember();
        }),
    });
  }

  /**
   * "Share with someone" — share a link to the page hosting the widget (where a
   * friend can book the same class) via the native share sheet, falling back to
   * copying the link. No canonical per-class URL exists, so we share the host
   * page URL + the class name.
   */
  private shareBooking(): void {
    const when = this.isAppt ? this.apptWhen : this.classWhen;
    const title =
      this.displayTitle ||
      (this.isAppt ? "Appointment booking" : "Class booking");
    const url =
      (typeof document !== "undefined" && document.referrer) ||
      (typeof window !== "undefined" ? window.location.href : "");
    const text = `Join me for ${this.displayTitle}${when ? ` — ${when}` : ""}`;
    const nav = typeof navigator !== "undefined" ? navigator : undefined;
    if (nav?.share) {
      void nav.share({ title, text, url }).catch(() => {});
    } else if (nav?.clipboard) {
      void nav.clipboard.writeText(url ? `${text} ${url}` : text);
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "zipper-booking-checkout": ZipperBookingCheckout;
  }
}
